/**
 * AdminDashboard — single-file drop-in component
 *
 * Usage (standalone with built-in static data):
 *   <AdminDashboard />
 *
 * Usage (data-driven integration):
 *   <AdminDashboard
 *     data={{ tasks: myTasks, cueNotes: myCueNotes, ... }}
 *     callbacks={{ onUpdateTask: (id, update) => api.updateTask(id, update) }}
 *   />
 *
 * Dependencies: react, @mui/material, @mui/icons-material, recharts, date-fns
 * Primary colour: #3f2d8f — find-replace to customise.
 */

import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormProvider, useForm } from "react-hook-form";
import {
  Box, Card, CardContent, Grid, Stack, Typography, Chip, Avatar,
  LinearProgress, Tooltip, Skeleton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Toolbar, Button, IconButton, TextField,
  Select, MenuItem, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, Divider, List, ListItem,
  ListItemAvatar, ListItemText, Tabs, Tab,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import type { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { XGrid } from "app/components/Common/XGrid/XGrid";
import { XGridChipCell } from "app/components/Common/XGrid/cells/XGridChipCell";
import {
  defaultExternalTasksFilterValues,
  DashboardExternalTasksFilter,
  DashboardExternalTasksChips,
  defaultCueNotesFilterValues,
  DashboardCueNotesFilter,
  DashboardCueNotesChips,
  defaultCommentsFilterValues,
  DashboardCommentsFilter,
  DashboardCommentsChips,
} from "./DashboardFilter";
import AssignmentIcon from "@mui/icons-material/Assignment";
import NotesIcon from "@mui/icons-material/Notes";
import CommentIcon from "@mui/icons-material/Comment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { DURATION_OPTIONS, DEFAULT_DURATION, type DurationValue } from "./durationUtils";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DownloadIcon from "@mui/icons-material/Download";
import ReplyIcon from "@mui/icons-material/Reply";
import PersonIcon from "@mui/icons-material/Person";
import CancelIcon from "@mui/icons-material/Cancel";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TimerIcon from "@mui/icons-material/Timer";
import SpeedIcon from "@mui/icons-material/Speed";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import PeopleIcon from "@mui/icons-material/People";
import UpdateIcon from "@mui/icons-material/Update";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, Legend,
  LineChart, Line,
} from "recharts";
import { formatDistanceToNow, format, isPast, isToday, isTomorrow } from "date-fns";
import { taskDetailsActions } from "app/components/Tasks/store/Tasks.redux";
import {
  selectShowStatusModal,
  selectedIsExternalTaskUpdated,
  selectTaskStatusUpdateFlag,
} from "app/components/Tasks/store/Tasks.selectors";
import { ExternalTaskStatusUpdateModal } from "app/components/Tasks/TaskDetails/ExternalTaskStatusUpdateModal";
import { EditExternalTaskModal } from "app/components/Tasks/EditTask/EditExternalTaskModal";
import { TaskStatus } from "app/models/Tasks/Tasks";
import { DashboardSmsTab } from "./DashboardSmsTab";

// ─────────────────────────────────────────────────────────────────────────────
// CONTAINER-WIDTH CONTEXT
// All layout breakpoints use this measured container width, not the viewport.
// This makes the dashboard truly responsive when dropped into any parent container.
// ─────────────────────────────────────────────────────────────────────────────

const DashboardWidthCtx = React.createContext(1200);
function useCW() { return React.useContext(DashboardWidthCtx); }

// ─────────────────────────────────────────────────────────────────────────────
// TYPE DEFINITIONS (all exported for consumer TypeScript use)
// ─────────────────────────────────────────────────────────────────────────────

export interface ExternalTask {
  id: string;
  placementId: number;
  rootTaskId?: number;
  candidateId?: number;
  candidateName?: string;
  taskNameId?: number;
  taskName: string;
  taskCategoryId?: number;
  taskCategory?: string;
  statusId: number;
  statusName: string;
  dueDate?: string;
  completedDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  lastUpdatedByName?: string;
}

export interface CueNote {
  id?: string;
  taskId: number;
  rootTaskId?: number;
  cueNoteTaskId?: number;
  placementId: number;
  senderId?: number;
  senderName: string;
  recipientId?: number;
  recipientName?: string;
  text: string;
  isReplied: boolean;
  isReply?: boolean;
  repliedAt?: string;
  repliedById?: number;
  repliedByName?: string;
  createdAt: string;
  dueDate?: string;
  statusId?: number;
  statusName?: string;
}

export interface Comment {
  taskId: number;
  rootTaskId?: number;
  placementId?: number;
  authorId?: number;
  authorName: string;
  text: string;
  isEdited: boolean;
  editedAt?: string;
  createdAt: string;
  entityType?: string;
  entityId?: number;
}

export interface ActivityEntry {
  id: string;
  entityType: "external_task" | "cue_note" | "comment";
  entityId?: string;
  action: string;
  description: string;
  performedBy: string;
  performedAt: string;
  /** Placement ID for navigation (cue_note, external_task). */
  placementId?: number;
  /** Root task ID for navigation (cue_note, comment). */
  rootTaskId?: number;
  /** Task ID for cue_note/comment - direct navigation. */
  taskId?: number;
}

export interface DashboardOverview {
  totalExternalTasks: number;
  openExternalTasks: number;
  completedExternalTasks: number;
  cancelledExternalTasks: number;
  totalCueNotes: number;
  repliedCueNotes: number;
  pendingCueNotes: number;
  totalComments: number;
  recentComments: number;
  /** Percent change vs last month for external tasks (e.g. 8 = +8%, -3 = -3%). */
  externalTasksTrendPercent?: number;
  /** Percent change vs last month for cue notes. */
  cueNotesTrendPercent?: number;
  /** Percent change vs last month for task completion. */
  taskCompletionTrendPercent?: number;
  /** Percent change vs last month for open actions. */
  openActionsTrendPercent?: number;
  taskStatusBreakdown: { statusId: number; statusName: string; count: number; color: string }[];
  taskMonthlyTrend: { month: string; count: number }[];
  cueNoteMonthlyTrend: { month: string; count: number }[];
}

export interface TaskStats {
  byStatus: { statusId: number; statusName: string; count: number; color: string }[];
  byCategory: { categoryId: number; categoryName: string; count: number }[];
  overdueTasks: number;
  tasksCompletedThisMonth: number;
}

export interface CueNoteStats {
  total: number; replied: number; pending: number;
  overdue: number; createdThisMonth: number; replyRate: number;
}

export interface CommentStats {
  total: number; createdThisMonth: number; createdThisWeek: number;
  topCommenters: { authorId?: number; authorName: string; count: number }[];
}

export interface TaskPerformanceReport {
  completionRate: number; avgCycleTimeDays: number;
  overdueCount: number; overdueRate: number;
  completedThisMonth: number; totalTasks: number;
  taskTypeBreakdown: { taskName: string; new: number; pending: number; inProgress: number; completed: number; cancelled: number; onHold: number; total: number }[];
  categoryRates: { category: string; total: number; completed: number; cancelled: number; rate: number }[];
  weeklyTrend: { week: string; completed: number; created: number }[];
}

export interface CueNoteSlaReport {
  slaComplianceRate: number; avgReplyHours: number;
  overdueNotes: number; totalThisMonth: number;
  slaTiers: { label: string; count: number; color: string }[];
  topResponders: { name: string; count: number; avgHours: number }[];
  weeklyReply: { week: string; created: number; replied: number }[];
}

export interface PlacementRiskReport {
  riskSummary: { high: number; medium: number; low: number };
  placements: {
    placementId: number; candidateName: string;
    totalTasks: number; openTasks: number; completedTasks: number;
    cancelledTasks: number; overdueTasks: number;
    completionRate: number; riskLevel: "high" | "medium" | "low";
  }[];
}

export interface StaffActivityReport {
  staffActivity: { name: string; total: number; completed: number; created: number; updated: number; cancelled: number; lastActive: string | null }[];
  actionBreakdown: { action: string; count: number }[];
  dailyActivity: { day: string; count: number; tasks: number; notes: number; comments: number }[];
}

export interface AdminDashboardData {
  overview?: DashboardOverview;
  categoryBreakdown?: { categoryId: number; categoryName: string; count: number }[];
  tasks?: ExternalTask[];
  cueNotes?: CueNote[];
  comments?: Comment[];
  /** Total count for server-side pagination (external tasks grid). */
  tasksTotal?: number;
  /** Total count for server-side pagination (cue notes grid). */
  cueNotesTotal?: number;
  /** Total count for server-side pagination (comments grid). */
  commentsTotal?: number;
  activityLog?: ActivityEntry[];
  taskStats?: TaskStats;
  cueNoteStats?: CueNoteStats;
  commentStats?: CommentStats;
  taskPerformanceReport?: TaskPerformanceReport;
  cueNoteSlaReport?: CueNoteSlaReport;
  placementRiskReport?: PlacementRiskReport;
  staffActivityReport?: StaffActivityReport;
  /** Recruiters for SMS tab dropdown (from getActiveRecruitersByLoggedInUserId). */
  smsRecruiters?: { userId?: number; firstName?: string; lastName?: string }[];
}

export interface AdminDashboardCallbacks {
  onCreateTask?: (data: Partial<ExternalTask>) => Promise<void>;
  onUpdateTask?: (id: string, data: Partial<ExternalTask>) => Promise<void>;
  onDeleteTask?: (id: string) => Promise<void>;
  onBulkUpdateTaskStatus?: (ids: string[], statusId: number) => Promise<void>;
  onBulkCancelTasksByPlacement?: (placementId: number) => Promise<void>;
  onCopyTasks?: (fromPlacementId: number, toPlacementId: number) => Promise<void>;
  onMarkCueNoteReplied?: (id: number, replyText: string, repliedByName: string) => Promise<void>;
  onCreateCueNote?: (data: Partial<CueNote>) => Promise<void>;
  onUpdateCueNote?: (id: number, data: Partial<CueNote>) => Promise<void>;
  onDeleteCueNote?: (id: number) => Promise<void>;
  onBulkCreateCueNotes?: (placementIds: number[], text: string) => Promise<void>;
  onBulkCancelCueNotes?: (ids: number[]) => Promise<void>;
  onCreateComment?: (data: Partial<Comment>) => Promise<void>;
  onUpdateComment?: (id: number, text: string) => Promise<void>;
  onDeleteComment?: (id: number) => Promise<void>;
  onBulkDeleteComments?: (ids: number[]) => Promise<void>;
}

export interface DateRangeFilter {
  startDate?: string;
  endDate?: string;
}

export interface AdminDashboardProps {
  /** All data for the dashboard. Defaults to built-in static sample data. */
  data?: AdminDashboardData;
  /** Async callbacks for CRUD operations. Omit any to use built-in optimistic local state. */
  callbacks?: AdminDashboardCallbacks;
  /** Starting tab. Default: "overview" */
  defaultTab?: "overview" | "external-tasks" | "cue-notes" | "comments" | "sms" | "reports" | "activity";
  /** Show the tab navigation bar (default true). Set false when your app provides its own navigation. */
  showNavigation?: boolean;
  /** Date range for filtering dashboard data (e.g. Recent Activity). ISO date strings. */
  dateRange?: DateRangeFilter;
  /** Callback when user changes the date range. Triggers data refetch. */
  onDateRangeChange?: (range: DateRangeFilter) => void;
  /** Callback when user switches tabs. Use for lazy-loading tab-specific data. */
  onTabChange?: (tab: "overview" | "external-tasks" | "cue-notes" | "comments" | "sms" | "reports" | "activity") => void;
  /** Duration filter (Today, 1 week, etc.). When provided with onDurationChange, used as controlled value. */
  duration?: DurationValue;
  /** Callback when user changes duration. Passes (duration, activeTab) so parent can reload current tab. */
  onDurationChange?: (duration: DurationValue, activeTab?: string) => void;
  /** Callback when user changes page/filters/sort in External Tasks grid. (pageNumber 1-based, pageSize, optional filters) */
  onFetchTasksPage?: (pageNumber: number, pageSize: number, filters?: { statusIds?: number[]; search?: string; placementId?: number; candidateId?: number; candidateName?: string; startDate?: string; endDate?: string; sortBy?: string; sortDir?: string }) => void | Promise<void>;
  /** Callback when user changes page/filters in Cue Notes grid. (pageNumber 1-based, pageSize, optional filters) */
  onFetchCueNotesPage?: (pageNumber: number, pageSize: number, filters?: { isReplied?: boolean; search?: string; startDate?: string; endDate?: string; sortBy?: string; sortDir?: string }) => void | Promise<void>;
  /** Callback when user changes page/filters in Comments grid. (pageNumber 1-based, pageSize, optional filters) */
  onFetchCommentsPage?: (pageNumber: number, pageSize: number, filters?: { search?: string; placementId?: number; taskId?: number; rootTaskId?: number; startDate?: string; endDate?: string; sortBy?: string; sortDir?: string }) => void | Promise<void>;
  /** Tab whose full data is currently loading. Show skeleton for that tab's widgets. */
  loadingTab?: "overview" | "external-tasks" | "cue-notes" | "comments" | "sms" | "reports" | "activity" | null;
  /** Tab whose grid is loading (pagination/filter). Show skeleton for that tab's grid only. */
  loadingGrid?: "overview" | "external-tasks" | "cue-notes" | "comments" | "sms" | "reports" | "activity" | null;
  /** Overview endpoints that have resolved — per-widget skeletons. When provided, each widget shows skeleton until its source is in this set. */
  overviewResolvedSources?: ReadonlySet<string>;
  /** Reports endpoints that have resolved — per-sub-tab skeletons. When provided, each sub-tab shows skeleton until its source is in this set. */
  reportsResolvedSources?: ReadonlySet<string>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const PRIMARY = "#3f2d8f";

const STATUS_MAP: Record<number, string> = {
  1: "New", 2: "Pending", 3: "In Progress", 4: "Completed", 5: "Cancelled", 6: "On Hold",
};
const STATUS_COLOR: Record<number, string> = {
  1: "#0288d1", 2: "#f59e0b", 3: "#7c3aed", 4: "#16a34a", 5: "#dc2626", 6: "#6b7280",
};
const CHART_COLORS = ["#3f2d8f", "#0288d1", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#ec4899", "#6b7280"];

// ─────────────────────────────────────────────────────────────────────────────
// STATIC DEFAULT DATA
// ─────────────────────────────────────────────────────────────────────────────

function dAgo(n: number) {
  const dt = new Date("2026-03-13T10:00:00.000Z");
  dt.setDate(dt.getDate() - n);
  return dt.toISOString();
}
function dFwd(n: number) {
  const dt = new Date("2026-03-13T10:00:00.000Z");
  dt.setDate(dt.getDate() + n);
  return dt.toISOString().slice(0, 10);
}

const DEFAULT_TASKS: ExternalTask[] = [
  { id:"t1",  placementId:1001,candidateId:101,candidateName:"Maria Garcia",    taskName:"Background Check",    taskCategory:"Compliance",    statusId:4,statusName:"Completed",   dueDate:dFwd(-5), completedDate:dAgo(3),  notes:"All clear.",             createdAt:dAgo(30),updatedAt:dAgo(3)  },
  { id:"t2",  placementId:1001,candidateId:101,candidateName:"Maria Garcia",    taskName:"Drug Screen",          taskCategory:"Compliance",    statusId:4,statusName:"Completed",   dueDate:dFwd(-3), completedDate:dAgo(1),  notes:"Negative.",              createdAt:dAgo(28),updatedAt:dAgo(1)  },
  { id:"t3",  placementId:1001,candidateId:101,candidateName:"Maria Garcia",    taskName:"I-9 Verification",     taskCategory:"Onboarding",    statusId:3,statusName:"In Progress",dueDate:dFwd(2),                          notes:"Docs submitted.",        createdAt:dAgo(20),updatedAt:dAgo(2)  },
  { id:"t4",  placementId:1002,candidateId:102,candidateName:"James Wilson",    taskName:"Background Check",    taskCategory:"Compliance",    statusId:3,statusName:"In Progress",dueDate:dFwd(-2),                          notes:"Awaiting 3rd party.",    createdAt:dAgo(25),updatedAt:dAgo(4)  },
  { id:"t5",  placementId:1002,candidateId:102,candidateName:"James Wilson",    taskName:"TB Test",              taskCategory:"Compliance",    statusId:2,statusName:"Pending",     dueDate:dFwd(-4),                          notes:"",                       createdAt:dAgo(22),updatedAt:dAgo(5)  },
  { id:"t6",  placementId:1002,candidateId:102,candidateName:"James Wilson",    taskName:"Orientation",          taskCategory:"Onboarding",    statusId:1,statusName:"New",         dueDate:dFwd(7),                           notes:"",                       createdAt:dAgo(10),updatedAt:dAgo(10) },
  { id:"t7",  placementId:1003,candidateId:103,candidateName:"Sarah Chen",      taskName:"License Verification", taskCategory:"Licensing",     statusId:4,statusName:"Completed",   dueDate:dFwd(-10),completedDate:dAgo(8),  notes:"RN license verified.",   createdAt:dAgo(35),updatedAt:dAgo(8)  },
  { id:"t8",  placementId:1003,candidateId:103,candidateName:"Sarah Chen",      taskName:"Contract Review",     taskCategory:"Documentation", statusId:4,statusName:"Completed",   dueDate:dFwd(-8), completedDate:dAgo(6),  notes:"Signed and filed.",      createdAt:dAgo(32),updatedAt:dAgo(6)  },
  { id:"t9",  placementId:1003,candidateId:103,candidateName:"Sarah Chen",      taskName:"Drug Screen",          taskCategory:"Compliance",    statusId:2,statusName:"Pending",     dueDate:dFwd(-1),                          notes:"",                       createdAt:dAgo(18),updatedAt:dAgo(7)  },
  { id:"t10", placementId:1004,candidateId:104,candidateName:"Michael Brown",   taskName:"Reference Check",     taskCategory:"Documentation", statusId:5,statusName:"Cancelled",   dueDate:dFwd(-12),                         notes:"Candidate withdrew.",    createdAt:dAgo(40),updatedAt:dAgo(11) },
  { id:"t11", placementId:1004,candidateId:104,candidateName:"Michael Brown",   taskName:"I-9 Verification",     taskCategory:"Onboarding",    statusId:1,statusName:"New",         dueDate:dFwd(5),                           notes:"",                       createdAt:dAgo(12),updatedAt:dAgo(12) },
  { id:"t12", placementId:1004,candidateId:104,candidateName:"Michael Brown",   taskName:"Background Check",    taskCategory:"Compliance",    statusId:6,statusName:"On Hold",     dueDate:dFwd(3),                           notes:"Waiting on candidate.",  createdAt:dAgo(15),updatedAt:dAgo(9)  },
  { id:"t13", placementId:1005,candidateId:105,candidateName:"Emily Davis",     taskName:"TB Test",              taskCategory:"Compliance",    statusId:4,statusName:"Completed",   dueDate:dFwd(-6), completedDate:dAgo(4),  notes:"Negative.",              createdAt:dAgo(28),updatedAt:dAgo(4)  },
  { id:"t14", placementId:1005,candidateId:105,candidateName:"Emily Davis",     taskName:"Orientation",          taskCategory:"Onboarding",    statusId:4,statusName:"Completed",   dueDate:dFwd(-4), completedDate:dAgo(2),  notes:"Completed on time.",     createdAt:dAgo(25),updatedAt:dAgo(2)  },
  { id:"t15", placementId:1005,candidateId:105,candidateName:"Emily Davis",     taskName:"License Verification", taskCategory:"Licensing",     statusId:3,statusName:"In Progress",dueDate:dFwd(4),                           notes:"Awaiting state response.",createdAt:dAgo(20),updatedAt:dAgo(5) },
  { id:"t16", placementId:1006,candidateId:106,candidateName:"Robert Johnson",  taskName:"Background Check",    taskCategory:"Compliance",    statusId:2,statusName:"Pending",     dueDate:dFwd(-3),                          notes:"",                       createdAt:dAgo(20),updatedAt:dAgo(6)  },
  { id:"t17", placementId:1006,candidateId:106,candidateName:"Robert Johnson",  taskName:"Contract Review",     taskCategory:"Documentation", statusId:1,statusName:"New",         dueDate:dFwd(8),                           notes:"",                       createdAt:dAgo(8), updatedAt:dAgo(8)  },
  { id:"t18", placementId:1006,candidateId:106,candidateName:"Robert Johnson",  taskName:"Drug Screen",          taskCategory:"Compliance",    statusId:3,statusName:"In Progress",dueDate:dFwd(-1),                          notes:"Lab processing.",        createdAt:dAgo(14),updatedAt:dAgo(3)  },
  { id:"t19", placementId:1007,candidateId:107,candidateName:"Lisa Martinez",   taskName:"I-9 Verification",     taskCategory:"Onboarding",    statusId:4,statusName:"Completed",   dueDate:dFwd(-9), completedDate:dAgo(7),  notes:"Docs verified.",         createdAt:dAgo(30),updatedAt:dAgo(7)  },
  { id:"t20", placementId:1007,candidateId:107,candidateName:"Lisa Martinez",   taskName:"License Verification", taskCategory:"Licensing",     statusId:4,statusName:"Completed",   dueDate:dFwd(-7), completedDate:dAgo(5),  notes:"LPN license valid.",     createdAt:dAgo(28),updatedAt:dAgo(5)  },
  { id:"t21", placementId:1007,candidateId:107,candidateName:"Lisa Martinez",   taskName:"Reference Check",     taskCategory:"Documentation", statusId:2,statusName:"Pending",     dueDate:dFwd(-2),                          notes:"",                       createdAt:dAgo(16),updatedAt:dAgo(8)  },
  { id:"t22", placementId:1008,candidateId:108,candidateName:"David Thompson",  taskName:"Background Check",    taskCategory:"Compliance",    statusId:1,statusName:"New",         dueDate:dFwd(6),                           notes:"",                       createdAt:dAgo(5), updatedAt:dAgo(5)  },
  { id:"t23", placementId:1008,candidateId:108,candidateName:"David Thompson",  taskName:"TB Test",              taskCategory:"Compliance",    statusId:1,statusName:"New",         dueDate:dFwd(10),                          notes:"",                       createdAt:dAgo(5), updatedAt:dAgo(5)  },
  { id:"t24", placementId:1008,candidateId:108,candidateName:"David Thompson",  taskName:"Orientation",          taskCategory:"Onboarding",    statusId:1,statusName:"New",         dueDate:dFwd(14),                          notes:"",                       createdAt:dAgo(5), updatedAt:dAgo(5)  },
  { id:"t25", placementId:1009,candidateId:109,candidateName:"Jessica Lee",     taskName:"Drug Screen",          taskCategory:"Compliance",    statusId:5,statusName:"Cancelled",   dueDate:dFwd(-15),                         notes:"Position filled.",       createdAt:dAgo(45),updatedAt:dAgo(13) },
  { id:"t26", placementId:1009,candidateId:109,candidateName:"Jessica Lee",     taskName:"Contract Review",     taskCategory:"Documentation", statusId:4,statusName:"Completed",   dueDate:dFwd(-11),completedDate:dAgo(10), notes:"All documents signed.",  createdAt:dAgo(38),updatedAt:dAgo(10) },
  { id:"t27", placementId:1009,candidateId:109,candidateName:"Jessica Lee",     taskName:"License Verification", taskCategory:"Licensing",     statusId:6,statusName:"On Hold",     dueDate:dFwd(1),                           notes:"Renewal in process.",    createdAt:dAgo(18),updatedAt:dAgo(6)  },
  { id:"t28", placementId:1010,candidateId:110,candidateName:"Christopher White",taskName:"Background Check",   taskCategory:"Compliance",    statusId:3,statusName:"In Progress",dueDate:dFwd(-3),                          notes:"Dispute on record.",     createdAt:dAgo(22),updatedAt:dAgo(4)  },
  { id:"t29", placementId:1010,candidateId:110,candidateName:"Christopher White",taskName:"I-9 Verification",    taskCategory:"Onboarding",    statusId:2,statusName:"Pending",     dueDate:dFwd(-5),                          notes:"",                       createdAt:dAgo(18),updatedAt:dAgo(7)  },
  { id:"t30", placementId:1010,candidateId:110,candidateName:"Christopher White",taskName:"Reference Check",    taskCategory:"Documentation", statusId:1,statusName:"New",         dueDate:dFwd(3),                           notes:"",                       createdAt:dAgo(10),updatedAt:dAgo(10) },
  { id:"t31", placementId:1001,candidateId:111,candidateName:"Amanda Taylor",   taskName:"Orientation",          taskCategory:"Onboarding",    statusId:4,statusName:"Completed",   dueDate:dFwd(-14),completedDate:dAgo(12), notes:"Completed day 1.",       createdAt:dAgo(40),updatedAt:dAgo(12) },
  { id:"t32", placementId:1002,candidateId:112,candidateName:"Daniel Harris",   taskName:"License Verification", taskCategory:"Licensing",     statusId:2,statusName:"Pending",     dueDate:dFwd(-6),                          notes:"Application submitted.", createdAt:dAgo(30),updatedAt:dAgo(9)  },
  { id:"t33", placementId:1003,candidateId:111,candidateName:"Amanda Taylor",   taskName:"Reference Check",     taskCategory:"Documentation", statusId:4,statusName:"Completed",   dueDate:dFwd(-18),completedDate:dAgo(16), notes:"3 refs confirmed.",      createdAt:dAgo(50),updatedAt:dAgo(16) },
  { id:"t34", placementId:1004,candidateId:112,candidateName:"Daniel Harris",   taskName:"Contract Review",     taskCategory:"Documentation", statusId:3,statusName:"In Progress",dueDate:dFwd(-4),                          notes:"Legal review pending.",  createdAt:dAgo(20),updatedAt:dAgo(5)  },
  { id:"t35", placementId:1005,candidateId:111,candidateName:"Amanda Taylor",   taskName:"TB Test",              taskCategory:"Compliance",    statusId:1,statusName:"New",         dueDate:dFwd(9),                           notes:"",                       createdAt:dAgo(3), updatedAt:dAgo(3)  },
  { id:"t36", placementId:1006,candidateId:112,candidateName:"Daniel Harris",   taskName:"Drug Screen",          taskCategory:"Compliance",    statusId:6,statusName:"On Hold",     dueDate:dFwd(2),                           notes:"Rescheduled.",           createdAt:dAgo(7), updatedAt:dAgo(2)  },
];

const DEFAULT_CUE_NOTES: CueNote[] = [
  { taskId:1,  rootTaskId:1,  placementId:1001, senderId:201, senderName:"Jennifer Smith",  text:"Background check delayed by county — please follow up with candidate to resubmit consent form.", isReplied:true,  repliedAt:dAgo(2), repliedByName:"James Wilson",    createdAt:dAgo(5),  dueDate:dFwd(-2) },
  { taskId:2,  rootTaskId:2,  placementId:1001, senderId:202, senderName:"Michael Johnson", text:"Drug screen results still pending from lab. Escalate if no update by EOD.", isReplied:true,  repliedAt:dAgo(1), repliedByName:"Sarah Williams",  createdAt:dAgo(4),  dueDate:dFwd(-1) },
  { taskId:3,  rootTaskId:3,  placementId:1002, senderId:203, senderName:"Sarah Williams",  text:"TB test appointment missed — candidate needs to reschedule ASAP. Clinic closes Friday.", isReplied:false, createdAt:dAgo(3),  dueDate:dFwd(-3) },
  { taskId:4,  rootTaskId:4,  placementId:1002, senderId:201, senderName:"Jennifer Smith",  text:"Reference check — third reference not answering calls. Try email outreach.", isReplied:true,  repliedAt:dAgo(0), repliedByName:"Robert Davis",    createdAt:dAgo(6),  dueDate:dFwd(1)  },
  { taskId:5,  rootTaskId:5,  placementId:1003, senderId:204, senderName:"Robert Davis",    text:"I-9 document has a discrepancy on the expiration date. Needs correction before onboarding.", isReplied:false, createdAt:dAgo(2),  dueDate:dFwd(-1) },
  { taskId:6,  rootTaskId:6,  placementId:1003, senderId:205, senderName:"Emily Brown",     text:"License verification — state board website is down. Will retry tomorrow morning.", isReplied:true,  repliedAt:dAgo(1), repliedByName:"Jennifer Smith",  createdAt:dAgo(5),  dueDate:dFwd(0)  },
  { taskId:7,  rootTaskId:7,  placementId:1004, senderId:202, senderName:"Michael Johnson", text:"Contract has a rate discrepancy — please review Section 4 with the client before signing.", isReplied:false, createdAt:dAgo(1),  dueDate:dFwd(-2) },
  { taskId:8,  rootTaskId:8,  placementId:1004, senderId:203, senderName:"Sarah Williams",  text:"Orientation scheduled for Monday — please confirm candidate availability and send location details.", isReplied:true,  repliedAt:dAgo(3), repliedByName:"Maria Garcia",    createdAt:dAgo(7),  dueDate:dFwd(3)  },
  { taskId:9,  rootTaskId:9,  placementId:1005, senderId:201, senderName:"Jennifer Smith",  text:"Background check returned a minor traffic infraction — needs client approval before proceeding.", isReplied:false, createdAt:dAgo(4),  dueDate:dFwd(-4) },
  { taskId:10, rootTaskId:10, placementId:1005, senderId:204, senderName:"Robert Davis",    text:"Drug screen collection site unavailable today. Backup location sent to candidate.", isReplied:true,  repliedAt:dAgo(1), repliedByName:"Emily Brown",     createdAt:dAgo(3),  dueDate:dFwd(-1) },
  { taskId:11, rootTaskId:11, placementId:1006, senderId:205, senderName:"Emily Brown",     text:"I-9 expires in 30 days — candidate needs to bring updated documentation to the office.", isReplied:false, createdAt:dAgo(2),  dueDate:dFwd(-2) },
  { taskId:12, rootTaskId:12, placementId:1006, senderId:202, senderName:"Michael Johnson", text:"License renewal application submitted — awaiting state board processing (3–5 business days).", isReplied:true,  repliedAt:dAgo(0), repliedByName:"Sarah Williams",  createdAt:dAgo(4),  dueDate:dFwd(2)  },
  { taskId:13, rootTaskId:13, placementId:1007, senderId:201, senderName:"Jennifer Smith",  text:"Placement start date moved up — need all compliance tasks completed by this Friday.", isReplied:false, createdAt:dAgo(1),  dueDate:dFwd(-1) },
  { taskId:14, rootTaskId:14, placementId:1007, senderId:203, senderName:"Sarah Williams",  text:"Reference from previous employer gave a mixed review — please discuss with recruiter.", isReplied:true,  repliedAt:dAgo(5), repliedByName:"Michael Johnson", createdAt:dAgo(10), dueDate:dFwd(-5) },
  { taskId:15, rootTaskId:15, placementId:1008, senderId:204, senderName:"Robert Davis",    text:"New hire background check vendor changed — please use updated portal link sent via email.", isReplied:false, createdAt:dAgo(0),  dueDate:dFwd(1)  },
  { taskId:16, rootTaskId:16, placementId:1008, senderId:205, senderName:"Emily Brown",     text:"TB test form needs physician signature — candidate's doctor requires 48h notice.", isReplied:true,  repliedAt:dAgo(2), repliedByName:"Jennifer Smith",  createdAt:dAgo(6),  dueDate:dFwd(-3) },
  { taskId:17, rootTaskId:17, placementId:1009, senderId:201, senderName:"Jennifer Smith",  text:"Contract addendum required for night shift differential — send to candidate for signature.", isReplied:false, createdAt:dAgo(2),  dueDate:dFwd(-5) },
  { taskId:18, rootTaskId:18, placementId:1009, senderId:202, senderName:"Michael Johnson", text:"Onboarding checklist incomplete — missing COVID vaccination record and health attestation.", isReplied:true,  repliedAt:dAgo(4), repliedByName:"Robert Davis",    createdAt:dAgo(9),  dueDate:dFwd(-2) },
  { taskId:19, rootTaskId:19, placementId:1010, senderId:203, senderName:"Sarah Williams",  text:"License verification blocked — candidate's license number entered incorrectly. Please correct.", isReplied:false, createdAt:dAgo(1),  dueDate:dFwd(-1) },
  { taskId:20, rootTaskId:20, placementId:1010, senderId:204, senderName:"Robert Davis",    text:"Drug screen results marked inconclusive — lab requests a retest within 72 hours.", isReplied:false, createdAt:dAgo(0),  dueDate:dFwd(1)  },
];

const DEFAULT_COMMENTS: Comment[] = [
  { taskId:1,  rootTaskId:1,  authorName:"Jennifer Smith",  text:"County records office confirmed they received the request. Expected turnaround 5–7 business days.", isEdited:false, createdAt:dAgo(29), entityType:"external_task", entityId:1  },
  { taskId:2,  rootTaskId:1,  authorName:"James Wilson",    text:"I called the lab — they said results should be ready by Thursday morning.", isEdited:true,  editedAt:dAgo(26), createdAt:dAgo(27), entityType:"external_task", entityId:1  },
  { taskId:3,  rootTaskId:3,  authorName:"Sarah Williams",  text:"I-9 documents received. Reviewing for accuracy before final approval.", isEdited:false, createdAt:dAgo(19), entityType:"external_task", entityId:5  },
  { taskId:4,  rootTaskId:3,  authorName:"Sarah Chen",      text:"Sent updated passport copy as requested. Let me know if you need anything else.", isEdited:false, createdAt:dAgo(18), entityType:"external_task", entityId:5  },
  { taskId:5,  rootTaskId:5,  authorName:"Jennifer Smith",  text:"Background check vendor portal is experiencing downtime. Will retry in 2 hours.", isEdited:false, createdAt:dAgo(24), entityType:"external_task", entityId:4  },
  { taskId:6,  rootTaskId:5,  authorName:"Michael Johnson", text:"Escalated to vendor support. They acknowledged the issue — ETA 4 hours for resolution.", isEdited:true,  editedAt:dAgo(22), createdAt:dAgo(23), entityType:"external_task", entityId:4  },
  { taskId:7,  rootTaskId:7,  authorName:"Robert Davis",    text:"Drug screen completed at backup location. Chain of custody form was signed.", isEdited:false, createdAt:dAgo(17), entityType:"external_task", entityId:9  },
  { taskId:8,  rootTaskId:7,  authorName:"Emily Davis",     text:"Just to confirm — the backup clinic address is 842 Harbor Blvd, Suite 12.", isEdited:false, createdAt:dAgo(16), entityType:"external_task", entityId:9  },
  { taskId:9,  rootTaskId:9,  authorName:"Emily Brown",     text:"License renewal submitted online. Tracking number: RN-2026-048271.", isEdited:false, createdAt:dAgo(34), entityType:"external_task", entityId:7  },
  { taskId:10, rootTaskId:9,  authorName:"Jennifer Smith",  text:"State board confirmed receipt. Processing time is approximately 10 business days.", isEdited:false, createdAt:dAgo(33), entityType:"external_task", entityId:7  },
  { taskId:11, rootTaskId:11, authorName:"Michael Johnson", text:"Contract clause 4b revised to reflect night shift rate. Sending for counter-signature.", isEdited:false, createdAt:dAgo(31), entityType:"external_task", entityId:8  },
  { taskId:12, rootTaskId:11, authorName:"Lisa Martinez",   text:"Counter-signed and returned via email. PDF attached in documents folder.", isEdited:false, createdAt:dAgo(30), entityType:"external_task", entityId:8  },
  { taskId:13, rootTaskId:13, authorName:"Sarah Williams",  text:"All three references contacted. Two responded positively. Third is unresponsive.", isEdited:true,  editedAt:dAgo(37), createdAt:dAgo(38), entityType:"external_task", entityId:19 },
  { taskId:14, rootTaskId:13, authorName:"Robert Davis",    text:"Tried calling the third reference twice. Left voicemail both times. Will try email.", isEdited:false, createdAt:dAgo(36), entityType:"external_task", entityId:19 },
  { taskId:15, rootTaskId:15, authorName:"Jennifer Smith",  text:"Orientation confirmed for 9 AM Monday. Conference room B is booked.", isEdited:false, createdAt:dAgo(24), entityType:"external_task", entityId:14 },
  { taskId:16, rootTaskId:15, authorName:"Emily Davis",     text:"I'll be there. Do I need to bring anything specific?", isEdited:false, createdAt:dAgo(23), entityType:"external_task", entityId:14 },
  { taskId:17, rootTaskId:17, authorName:"Emily Brown",     text:"I-9 re-verification completed. New document scanned and uploaded to records.", isEdited:false, createdAt:dAgo(27), entityType:"external_task", entityId:20 },
  { taskId:18, rootTaskId:17, authorName:"Michael Johnson", text:"Records team confirmed they received the updated document. File is now compliant.", isEdited:false, createdAt:dAgo(26), entityType:"external_task", entityId:20 },
  { taskId:19, rootTaskId:19, authorName:"Sarah Williams",  text:"Background check dispute resolved — infraction pre-dates look-back period. All clear.", isEdited:true,  editedAt:dAgo(20), createdAt:dAgo(21), entityType:"external_task", entityId:28 },
  { taskId:20, rootTaskId:19, authorName:"Robert Davis",    text:"Client notified and gave written approval to proceed with placement.", isEdited:false, createdAt:dAgo(19), entityType:"external_task", entityId:28 },
];

const DEFAULT_ACTIVITY: ActivityEntry[] = [
  { id:"a1",  entityType:"external_task",action:"completed",      description:"Completed Drug Screen for Maria Garcia",               performedBy:"Jennifer Smith",  performedAt:dAgo(1)  },
  { id:"a2",  entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1001",               performedBy:"Sarah Williams",  performedAt:dAgo(1)  },
  { id:"a3",  entityType:"external_task",action:"completed",      description:"Completed Orientation for Emily Davis",                performedBy:"Michael Johnson", performedAt:dAgo(2)  },
  { id:"a4",  entityType:"comment",       action:"created",        description:"Added comment on License Verification #t20",           performedBy:"Jennifer Smith",  performedAt:dAgo(2)  },
  { id:"a5",  entityType:"external_task",action:"status_changed", description:"I-9 Verification moved to In Progress for Placement #1001",performedBy:"Sarah Williams",performedAt:dAgo(2)},
  { id:"a6",  entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1002",               performedBy:"Robert Davis",    performedAt:dAgo(0)  },
  { id:"a7",  entityType:"external_task",action:"completed",      description:"Completed License Verification for Sarah Chen",        performedBy:"Robert Davis",    performedAt:dAgo(8)  },
  { id:"a8",  entityType:"external_task",action:"completed",      description:"Completed Contract Review for Sarah Chen",             performedBy:"Emily Brown",     performedAt:dAgo(6)  },
  { id:"a9",  entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1003",               performedBy:"Jennifer Smith",  performedAt:dAgo(1)  },
  { id:"a10", entityType:"external_task",action:"cancelled",      description:"Cancelled Reference Check — candidate withdrew",       performedBy:"Michael Johnson", performedAt:dAgo(11) },
  { id:"a11", entityType:"comment",       action:"created",        description:"Added comment on Drug Screen #t9",                     performedBy:"Robert Davis",    performedAt:dAgo(17) },
  { id:"a12", entityType:"external_task",action:"completed",      description:"Completed TB Test for Emily Davis",                    performedBy:"Sarah Williams",  performedAt:dAgo(4)  },
  { id:"a13", entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1004",               performedBy:"Maria Garcia",    performedAt:dAgo(3)  },
  { id:"a14", entityType:"external_task",action:"completed",      description:"Completed I-9 Verification for Lisa Martinez",         performedBy:"Jennifer Smith",  performedAt:dAgo(7)  },
  { id:"a15", entityType:"external_task",action:"completed",      description:"Completed License Verification for Lisa Martinez",     performedBy:"Emily Brown",     performedAt:dAgo(5)  },
  { id:"a16", entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1005",               performedBy:"Emily Brown",     performedAt:dAgo(1)  },
  { id:"a17", entityType:"comment",       action:"created",        description:"Added comment on I-9 Verification #t3",                performedBy:"Sarah Williams",  performedAt:dAgo(19) },
  { id:"a18", entityType:"external_task",action:"completed",      description:"Completed Contract Review for Jessica Lee",            performedBy:"Robert Davis",    performedAt:dAgo(10) },
  { id:"a19", entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1006",               performedBy:"Sarah Williams",  performedAt:dAgo(0)  },
  { id:"a20", entityType:"external_task",action:"completed",      description:"Completed Orientation for Amanda Taylor",              performedBy:"Michael Johnson", performedAt:dAgo(12) },
  { id:"a21", entityType:"comment",       action:"created",        description:"Added comment on Contract Review #t8",                 performedBy:"Michael Johnson", performedAt:dAgo(31) },
  { id:"a22", entityType:"external_task",action:"completed",      description:"Completed Reference Check for Amanda Taylor",          performedBy:"Emily Brown",     performedAt:dAgo(16) },
  { id:"a23", entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1007",               performedBy:"Michael Johnson", performedAt:dAgo(5)  },
  { id:"a24", entityType:"comment",       action:"updated",        description:"Edited comment on Background Check #t28",              performedBy:"Sarah Williams",  performedAt:dAgo(20) },
  { id:"a25", entityType:"external_task",action:"cancelled",      description:"Cancelled Drug Screen — position filled",              performedBy:"Robert Davis",    performedAt:dAgo(13) },
  { id:"a26", entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1008",               performedBy:"Jennifer Smith",  performedAt:dAgo(2)  },
  { id:"a27", entityType:"comment",       action:"created",        description:"Added comment on Background Check #t4",                performedBy:"Jennifer Smith",  performedAt:dAgo(24) },
  { id:"a28", entityType:"external_task",action:"status_changed", description:"Background Check moved to In Progress for Placement #1002",performedBy:"Michael Johnson",performedAt:dAgo(4)},
  { id:"a29", entityType:"cue_note",      action:"replied",        description:"Replied to cue note on Placement #1009",               performedBy:"Robert Davis",    performedAt:dAgo(4)  },
  { id:"a30", entityType:"external_task",action:"status_changed", description:"Background Check placed On Hold for Placement #1004",  performedBy:"Emily Brown",     performedAt:dAgo(9)  },
];

const DEFAULT_TASK_PERF: TaskPerformanceReport = {
  completionRate:33.3, avgCycleTimeDays:8.4, overdueCount:10, overdueRate:27.8, completedThisMonth:5, totalTasks:36,
  taskTypeBreakdown:[
    { taskName:"Background Check",    new:1,pending:2,inProgress:3,completed:3,cancelled:0,onHold:0,total:9 },
    { taskName:"Drug Screen",         new:0,pending:0,inProgress:1,completed:2,cancelled:1,onHold:1,total:5 },
    { taskName:"TB Test",             new:1,pending:1,inProgress:0,completed:2,cancelled:0,onHold:0,total:4 },
    { taskName:"Reference Check",     new:1,pending:1,inProgress:0,completed:2,cancelled:1,onHold:0,total:5 },
    { taskName:"I-9 Verification",    new:1,pending:1,inProgress:1,completed:2,cancelled:0,onHold:0,total:5 },
    { taskName:"Contract Review",     new:1,pending:0,inProgress:1,completed:2,cancelled:0,onHold:0,total:4 },
    { taskName:"Orientation",         new:1,pending:0,inProgress:0,completed:3,cancelled:0,onHold:0,total:4 },
    { taskName:"License Verification",new:0,pending:1,inProgress:1,completed:3,cancelled:0,onHold:1,total:6 },
  ],
  categoryRates:[
    { category:"Licensing",     total:6,  completed:3, cancelled:0, rate:50.0 },
    { category:"Onboarding",    total:8,  completed:4, cancelled:0, rate:50.0 },
    { category:"Compliance",    total:14, completed:5, cancelled:1, rate:35.7 },
    { category:"Documentation", total:6,  completed:2, cancelled:1, rate:33.3 },
    { category:"Billing",       total:2,  completed:0, cancelled:0, rate:0.0  },
  ],
  weeklyTrend:[
    { week:"Jan 05",completed:2,created:4  },{ week:"Jan 12",completed:3,created:5  },
    { week:"Jan 19",completed:4,created:6  },{ week:"Jan 26",completed:5,created:7  },
    { week:"Feb 02",completed:3,created:8  },{ week:"Feb 09",completed:4,created:5  },
    { week:"Feb 16",completed:5,created:9  },{ week:"Feb 23",completed:3,created:7  },
    { week:"Mar 02",completed:6,created:10 },{ week:"Mar 09",completed:5,created:6  },
  ],
};

const DEFAULT_SLA: CueNoteSlaReport = {
  slaComplianceRate:58.3, avgReplyHours:38.2, overdueNotes:7, totalThisMonth:8,
  slaTiers:[
    { label:"< 24 hours",    count:3, color:"#16a34a" },
    { label:"24 – 48 hours", count:4, color:"#0288d1" },
    { label:"48 – 72 hours", count:3, color:"#f59e0b" },
    { label:"> 72 hours",    count:2, color:"#dc2626" },
  ],
  topResponders:[
    { name:"Jennifer Smith",  count:4, avgHours:22.5 },
    { name:"Sarah Williams",  count:3, avgHours:31.0 },
    { name:"Michael Johnson", count:2, avgHours:44.2 },
    { name:"Robert Davis",    count:2, avgHours:28.8 },
    { name:"Emily Brown",     count:1, avgHours:52.1 },
  ],
  weeklyReply:[
    { week:"Jan 20",created:3,replied:2 },{ week:"Jan 27",created:4,replied:3 },
    { week:"Feb 03",created:5,replied:3 },{ week:"Feb 10",created:3,replied:2 },
    { week:"Feb 17",created:4,replied:4 },{ week:"Feb 24",created:6,replied:4 },
    { week:"Mar 03",created:5,replied:3 },{ week:"Mar 10",created:4,replied:2 },
  ],
};

const DEFAULT_RISK: PlacementRiskReport = {
  riskSummary:{ high:3, medium:4, low:3 },
  placements:[
    { placementId:1002,candidateName:"James Wilson",      totalTasks:4,openTasks:3,completedTasks:0,cancelledTasks:0,overdueTasks:3,completionRate:0.0,  riskLevel:"high"   },
    { placementId:1010,candidateName:"Christopher White", totalTasks:3,openTasks:3,completedTasks:0,cancelledTasks:0,overdueTasks:2,completionRate:0.0,  riskLevel:"high"   },
    { placementId:1006,candidateName:"Robert Johnson",    totalTasks:4,openTasks:3,completedTasks:0,cancelledTasks:0,overdueTasks:2,completionRate:0.0,  riskLevel:"high"   },
    { placementId:1004,candidateName:"Michael Brown",     totalTasks:4,openTasks:2,completedTasks:0,cancelledTasks:1,overdueTasks:1,completionRate:0.0,  riskLevel:"medium" },
    { placementId:1007,candidateName:"Lisa Martinez",     totalTasks:3,openTasks:1,completedTasks:2,cancelledTasks:0,overdueTasks:1,completionRate:66.7, riskLevel:"medium" },
    { placementId:1001,candidateName:"Maria Garcia",      totalTasks:4,openTasks:2,completedTasks:2,cancelledTasks:0,overdueTasks:1,completionRate:50.0, riskLevel:"medium" },
    { placementId:1009,candidateName:"Jessica Lee",       totalTasks:3,openTasks:1,completedTasks:1,cancelledTasks:1,overdueTasks:1,completionRate:33.3, riskLevel:"medium" },
    { placementId:1003,candidateName:"Sarah Chen",        totalTasks:3,openTasks:1,completedTasks:2,cancelledTasks:0,overdueTasks:0,completionRate:66.7, riskLevel:"low"    },
    { placementId:1005,candidateName:"Emily Davis",       totalTasks:4,openTasks:1,completedTasks:3,cancelledTasks:0,overdueTasks:0,completionRate:75.0, riskLevel:"low"    },
    { placementId:1008,candidateName:"David Thompson",    totalTasks:3,openTasks:3,completedTasks:0,cancelledTasks:0,overdueTasks:0,completionRate:0.0,  riskLevel:"low"    },
  ],
};

const DEFAULT_STAFF: StaffActivityReport = {
  staffActivity:[
    { name:"Jennifer Smith",  total:14, completed:5, created:6, updated:3, cancelled:0, lastActive:dAgo(0)  },
    { name:"Michael Johnson", total:12, completed:3, created:5, updated:3, cancelled:1, lastActive:dAgo(1)  },
    { name:"Sarah Williams",  total:10, completed:4, created:4, updated:2, cancelled:0, lastActive:dAgo(0)  },
    { name:"Robert Davis",    total:9,  completed:4, created:3, updated:2, cancelled:0, lastActive:dAgo(0)  },
    { name:"Emily Brown",     total:7,  completed:3, created:2, updated:1, cancelled:1, lastActive:dAgo(1)  },
  ],
  actionBreakdown:[
    { action:"completed",      count:19 },{ action:"created",   count:20 },
    { action:"status_changed", count:8  },{ action:"replied",   count:12 },
    { action:"updated",        count:11 },{ action:"cancelled", count:3  },
  ],
  dailyActivity:[
    { day:"Feb 28",count:4, tasks:2,notes:1,comments:1 },{ day:"Mar 01",count:6, tasks:3,notes:2,comments:1 },
    { day:"Mar 02",count:8, tasks:4,notes:2,comments:2 },{ day:"Mar 03",count:5, tasks:2,notes:2,comments:1 },
    { day:"Mar 04",count:7, tasks:3,notes:3,comments:1 },{ day:"Mar 05",count:3, tasks:1,notes:1,comments:1 },
    { day:"Mar 06",count:2, tasks:1,notes:1,comments:0 },{ day:"Mar 07",count:9, tasks:4,notes:3,comments:2 },
    { day:"Mar 08",count:11,tasks:5,notes:4,comments:2 },{ day:"Mar 09",count:7, tasks:3,notes:2,comments:2 },
    { day:"Mar 10",count:6, tasks:2,notes:3,comments:1 },{ day:"Mar 11",count:8, tasks:4,notes:2,comments:2 },
    { day:"Mar 12",count:5, tasks:2,notes:2,comments:1 },{ day:"Mar 13",count:4, tasks:2,notes:1,comments:1 },
  ],
};

const DEFAULT_MONTHLY_TREND = {
  task: [
    { month:"2025-10",count:22 },{ month:"2025-11",count:28 },{ month:"2025-12",count:19 },
    { month:"2026-01",count:31 },{ month:"2026-02",count:34 },{ month:"2026-03",count:36 },
  ],
  note: [
    { month:"2025-10",count:8  },{ month:"2025-11",count:11 },{ month:"2025-12",count:9  },
    { month:"2026-01",count:14 },{ month:"2026-02",count:18 },{ month:"2026-03",count:20 },
  ],
};

/** Empty fallbacks for reports — no static data, show zeros until API returns */
const EMPTY_TASK_PERF: TaskPerformanceReport = {
  completionRate: 0, avgCycleTimeDays: 0, overdueCount: 0, overdueRate: 0, completedThisMonth: 0, totalTasks: 0,
  taskTypeBreakdown: [], categoryRates: [], weeklyTrend: [],
};
const EMPTY_SLA: CueNoteSlaReport = {
  slaComplianceRate: 0, avgReplyHours: 0, overdueNotes: 0, totalThisMonth: 0,
  slaTiers: [
    { label: "< 24 hours", count: 0, color: "#16a34a" },
    { label: "24 – 48 hours", count: 0, color: "#0288d1" },
    { label: "48 – 72 hours", count: 0, color: "#f59e0b" },
    { label: "> 72 hours", count: 0, color: "#dc2626" },
  ],
  topResponders: [], weeklyReply: [],
};
const EMPTY_RISK: PlacementRiskReport = {
  riskSummary: { high: 0, medium: 0, low: 0 },
  placements: [],
};
const EMPTY_STAFF: StaffActivityReport = {
  staffActivity: [], actionBreakdown: [], dailyActivity: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

function paginate<T>(arr: T[], page: number, pageSize: number) {
  return arr.slice(page * pageSize, (page + 1) * pageSize);
}

function exportCsv(name: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => JSON.stringify(r[k] ?? "")).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = "data:text/csv," + encodeURIComponent(csv);
  a.download = `${name}.csv`;
  a.click();
}

function computeOverview(tasks: ExternalTask[], notes: CueNote[], comments: Comment[]): DashboardOverview {
  const statusBreakdown = [1,2,3,4,5,6].map((id) => ({
    statusId: id, statusName: STATUS_MAP[id], color: STATUS_COLOR[id],
    count: tasks.filter((t) => t.statusId === id).length,
  }));
  return {
    totalExternalTasks: tasks.length,
    openExternalTasks: tasks.filter((t) => ![4,5].includes(t.statusId)).length,
    completedExternalTasks: tasks.filter((t) => t.statusId === 4).length,
    cancelledExternalTasks: tasks.filter((t) => t.statusId === 5).length,
    totalCueNotes: notes.length,
    repliedCueNotes: notes.filter((n) => n.isReplied).length,
    pendingCueNotes: notes.filter((n) => !n.isReplied).length,
    totalComments: comments.length,
    recentComments: comments.filter((c) => new Date(c.createdAt) > new Date(Date.now() - 7*86400000)).length,
    taskStatusBreakdown: statusBreakdown,
    taskMonthlyTrend: DEFAULT_MONTHLY_TREND.task,
    cueNoteMonthlyTrend: DEFAULT_MONTHLY_TREND.note,
  };
}

function computeTaskStats(tasks: ExternalTask[]): TaskStats {
  const catMap: Record<string, number> = {};
  tasks.forEach((t) => { if (t.taskCategory) catMap[t.taskCategory] = (catMap[t.taskCategory] ?? 0) + 1; });
  return {
    byStatus: [1,2,3,4,5,6].map((id) => ({ statusId:id, statusName:STATUS_MAP[id], color:STATUS_COLOR[id], count:tasks.filter((t)=>t.statusId===id).length })),
    byCategory: Object.entries(catMap).map(([name,count],i) => ({ categoryId:i+1, categoryName:name, count })),
    overdueTasks: tasks.filter((t) => t.dueDate && new Date(t.dueDate)<new Date() && ![4,5].includes(t.statusId)).length,
    tasksCompletedThisMonth: tasks.filter((t) => t.statusId===4 && t.completedDate && new Date(t.completedDate)>new Date(Date.now()-30*86400000)).length,
  };
}

function computeCueNoteStats(notes: CueNote[]): CueNoteStats {
  const replied = notes.filter((n) => n.isReplied).length;
  const now = new Date();
  return {
    total: notes.length,
    replied,
    pending: notes.filter((n) => !n.isReplied).length,
    overdue: notes.filter((n) => n.dueDate && !n.isReplied && new Date(n.dueDate) < now).length,
    createdThisMonth: notes.filter((n) => new Date(n.createdAt) > new Date(now.getTime() - 30*86400000)).length,
    replyRate: notes.length ? Math.round((replied / notes.length) * 100) : 0,
  };
}

function computeCommentStats(comments: Comment[]): CommentStats {
  const now = new Date();
  const cnt: Record<string, number> = {};
  comments.forEach((c) => { cnt[c.authorName] = (cnt[c.authorName] ?? 0) + 1; });
  return {
    total: comments.length,
    createdThisMonth: comments.filter((c) => new Date(c.createdAt) > new Date(now.getTime() - 30*86400000)).length,
    createdThisWeek: comments.filter((c) => new Date(c.createdAt) > new Date(now.getTime() - 7*86400000)).length,
    topCommenters: Object.entries(cnt).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([name,count])=>({ authorName:name, count })),
  };
}

let _idSeq = 2000;
function nextId() { return ++_idSeq; }

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function ConfirmDlg({ open, title, message, confirmLabel = "Confirm", severity = "error", onConfirm, onClose, loading }: {
  open: boolean; title: string; message: string; confirmLabel?: string;
  severity?: "error" | "warning"; onConfirm: () => void; onClose: () => void; loading?: boolean;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>{title}</DialogTitle>
      <DialogContent><Typography variant="body2">{message}</Typography></DialogContent>
      <DialogActions sx={{ px:3, pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color={severity} disabled={loading} sx={{ textTransform:"none" }}>
          {loading ? "…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const DG_SX = {
  border: "1px solid", borderColor: "divider", borderRadius: 2, bgcolor: "background.paper",
  "& .MuiDataGrid-columnHeaders": { bgcolor: "#f8f9fb" },
  "& .MuiDataGrid-row.Mui-selected": { bgcolor: "#ede7f6" },
  "& .MuiDataGrid-row.Mui-selected:hover": { bgcolor: "#e8e0f7" },
  "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
} as const;
const DG_SX_BULK = { ...DG_SX, borderRadius: "0 0 8px 8px" } as const;

function StatCard({ title, value, subtitle, icon, iconBg="#e8eaf6", trend, loading, color }: {
  title: string; value: number|string; subtitle?: string; icon?: React.ReactNode;
  iconBg?: string; trend?: number; loading?: boolean; color?: string;
}) {
  const w = useCW();
  if (loading) return <Card sx={{ height:"100%" }}><CardContent><Skeleton width="40%" height={16} /><Skeleton width="60%" height={40} sx={{ mt:1 }} /></CardContent></Card>;
  return (
    <Card sx={{ height:"100%","&:hover":{ boxShadow:3 },transition:"box-shadow 0.2s" }}>
      <CardContent sx={{ p:2,"&:last-child":{ pb:2 } }}>
        <Box sx={{ display:"flex",alignItems:"flex-start",justifyContent:"space-between" }}>
          <Box sx={{ flex:1,minWidth:0 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform:"uppercase",letterSpacing:"0.05em" }}>{title}</Typography>
            <Typography variant="h4" fontWeight={700} sx={{ mt:0.5,color:color||"text.primary",lineHeight:1.2,fontSize:w>=600?"2.125rem":"1.4rem" }}>
              {typeof value==="number"?value.toLocaleString():value}
            </Typography>
            {subtitle && <Typography variant="caption" color="text.secondary" sx={{ mt:0.5,display:"block" }}>{subtitle}</Typography>}
            {trend!==undefined && (
              <Box sx={{ display:"flex",alignItems:"center",gap:0.5,mt:0.75 }}>
                {trend>=0 ? <TrendingUpIcon sx={{ fontSize:14,color:"success.main" }} /> : <TrendingDownIcon sx={{ fontSize:14,color:"error.main" }} />}
                <Typography variant="caption" fontWeight={600} color={trend>=0?"success.main":"error.main"}>{Math.abs(trend)}% vs last month</Typography>
              </Box>
            )}
          </Box>
          {icon && <Box sx={{ width:44,height:44,borderRadius:2,bgcolor:iconBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,ml:2 }}>{icon}</Box>}
        </Box>
      </CardContent>
    </Card>
  );
}

function StatusChipUI({ statusId, statusName }: { statusId: number; statusName: string }) {
  const color = STATUS_COLOR[statusId] ?? "#6b7280";
  const LABELS: Record<number,string> = { 1:"New",2:"Pending",3:"In Progress",4:"Completed",5:"Cancelled",6:"On Hold" };
  return <Chip label={statusName||LABELS[statusId]||"Unknown"} size="small" sx={{ bgcolor:`${color}18`,color,fontWeight:700,fontSize:"0.7rem",height:22 }} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// OVERVIEW TAB
// ─────────────────────────────────────────────────────────────────────────────

function RingProgress({ pct, size=60 }: { pct: number; size?: number }) {
  const r = (size-10)/2, circ = 2*Math.PI*r, dash = Math.min(pct/100,1)*circ;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth={6} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#fff" strokeWidth={6}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2+5} textAnchor="middle" fill="#fff" fontSize={13} fontWeight="bold">{Math.round(pct)}%</text>
    </svg>
  );
}

function HeroCard({ title, value, sub, gradient, icon, ring, trend, loading }: {
  title: string; value: number|string; sub: string; gradient: string;
  icon: React.ReactNode; ring?: number; trend?: number; loading?: boolean;
}) {
  const w = useCW();
  if (loading) return <Box sx={{ borderRadius:3,overflow:"hidden",background:gradient,p:2.5,minHeight:130 }}>
    <Skeleton variant="text" width="60%" sx={{ bgcolor:"rgba(255,255,255,0.3)" }} />
    <Skeleton variant="text" width="40%" height={50} sx={{ bgcolor:"rgba(255,255,255,0.3)",mt:1 }} />
  </Box>;
  return (
    <Box sx={{ borderRadius:3,overflow:"hidden",background:gradient,p:w>=600?3:2,position:"relative",boxShadow:"0 4px 20px rgba(0,0,0,0.15)",height:"100%",
      "&::after":{ content:'""',position:"absolute",top:-30,right:-30,width:120,height:120,borderRadius:"50%",background:"rgba(255,255,255,0.06)" } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex:1,minWidth:0 }}>
          <Typography variant="caption" sx={{ color:"rgba(255,255,255,0.75)",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.07em",fontSize:"0.67rem" }}>{title}</Typography>
          <Typography variant="h3" fontWeight={800} sx={{ color:"#fff",lineHeight:1.1,mt:0.5,fontSize:w>=600?"2.2rem":"1.7rem" }}>
            {typeof value==="number"?value.toLocaleString():value}
          </Typography>
          <Typography variant="body2" sx={{ color:"rgba(255,255,255,0.75)",mt:0.5,fontSize:"0.78rem" }}>{sub}</Typography>
          {trend!==undefined && (
            <Stack direction="row" spacing={0.5} alignItems="center" mt={1}>
              {trend>=0 ? <TrendingUpIcon sx={{ fontSize:14,color:"rgba(255,255,255,0.9)" }} /> : <TrendingDownIcon sx={{ fontSize:14,color:"rgba(255,255,255,0.6)" }} />}
              <Typography variant="caption" sx={{ color:"rgba(255,255,255,0.9)",fontWeight:700 }}>{Math.abs(trend)}% vs last month</Typography>
            </Stack>
          )}
        </Box>
        <Box sx={{ display:"flex",flexDirection:"column",alignItems:"center",gap:1,zIndex:1 }}>
          <Box sx={{ width:44,height:44,borderRadius:2,bgcolor:"rgba(255,255,255,0.18)",display:"flex",alignItems:"center",justifyContent:"center" }}>{icon}</Box>
          {ring!==undefined && <RingProgress pct={ring} />}
        </Box>
      </Stack>
    </Box>
  );
}

const URGENCY_COLOR: Record<string,string> = { overdue:"#dc2626",today:"#f59e0b",tomorrow:"#0288d1",upcoming:"#6b7280" };
const URGENCY_BG: Record<string,string>    = { overdue:"#fff5f5",today:"#fffbeb",tomorrow:"#e3f2fd",upcoming:"#f9fafb" };
function urgency(due?: string|null) {
  if (!due) return null;
  const d = new Date(due);
  if (isPast(d)) return "overdue";
  if (isToday(d)) return "today";
  if (isTomorrow(d)) return "tomorrow";
  return "upcoming";
}

function PriorityQueuePanel({ tasks, notes }: { tasks: ExternalTask[]; notes: CueNote[] }) {
  const items = useMemo(() => {
    const t = tasks.filter((t) => t.dueDate && [1,2,3].includes(t.statusId))
      .sort((a,b) => new Date(a.dueDate!).getTime()-new Date(b.dueDate!).getTime())
      .slice(0,4)
      .map((t) => ({ id:t.id,type:"task" as const,label:t.taskName,sub:`Placement #${t.placementId} · ${t.candidateName??""} `,due:t.dueDate,statusName:t.statusName,statusId:t.statusId }));
    const n = notes.filter((n) => n.dueDate&&!n.isReplied).slice(0,3)
      .map((n) => ({ id:String(n.taskId),type:"note" as const,label:`Cue #${n.taskId}: ${n.text.slice(0,40)}…`,sub:`Placement #${n.placementId} · ${n.senderName}`,due:n.dueDate,statusName:"Unreplied",statusId:2 }));
    return [...t,...n].sort((a,b)=>{ if(!a.due)return 1; if(!b.due)return -1; return new Date(a.due).getTime()-new Date(b.due).getTime(); });
  },[tasks,notes]);
  return (
    <Box sx={{ height:"100%",display:"flex",flexDirection:"column" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" fontWeight={700}>Needs Attention</Typography>
        {items.length>0 && <Chip label={`${items.length} items`} size="small" sx={{ bgcolor:"#fff5f5",color:"#dc2626",fontWeight:700,fontSize:"0.7rem",height:20 }} />}
      </Stack>
      {items.length===0 ? (
        <Box sx={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1.5 }}>
          <CheckCircleIcon sx={{ fontSize:40,color:"#16a34a",opacity:0.7 }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>All caught up!</Typography>
        </Box>
      ) : (
        <Stack spacing={1} sx={{ flex:1,overflowY:"auto" }}>
          {items.map((item) => {
            const u=urgency(item.due); const color=u?URGENCY_COLOR[u]:"#6b7280"; const bg=u?URGENCY_BG[u]:"#f9fafb";
            return (
              <Box key={item.id} sx={{ borderRadius:2,border:`1px solid ${color}22`,bgcolor:bg,p:1.25,borderLeft:`3px solid ${color}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box sx={{ flex:1,minWidth:0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" mb={0.25}>
                      {item.type==="task" ? <AssignmentIcon sx={{ fontSize:13,color }} /> : <NotesIcon sx={{ fontSize:13,color }} />}
                      <Typography variant="body2" fontWeight={600} noWrap>{item.label}</Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" noWrap>{item.sub}</Typography>
                  </Box>
                  <Box sx={{ ml:1,flexShrink:0,textAlign:"right" }}>
                    {item.due && <Typography variant="caption" fontWeight={700} sx={{ color }}>{u==="overdue"?"OVERDUE":u==="today"?"TODAY":u==="tomorrow"?"Tomorrow":new Date(item.due).toLocaleDateString()}</Typography>}
                    <Chip label={item.statusName} size="small" sx={{ display:"block",mt:0.25,height:18,fontSize:"0.63rem",bgcolor:`${STATUS_COLOR[item.statusId]??'#6b7280'}18`,color:STATUS_COLOR[item.statusId]??'#6b7280',fontWeight:700 }} />
                  </Box>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

function ActivityTimeline({ activity, limit = 50 }: { activity: ActivityEntry[]; limit?: number }) {
  const TC: Record<string,{ color: string; icon: React.ReactNode }> = {
    external_task:{ color:"#7c3aed",icon:<AssignmentIcon sx={{ fontSize:13 }} /> },
    cue_note:      { color:"#0288d1",icon:<NotesIcon sx={{ fontSize:13 }} /> },
    comment:       { color:"#16a34a",icon:<CommentIcon sx={{ fontSize:13 }} /> },
  };
  const AC: Record<string,string> = { created:"#16a34a",completed:"#0288d1",cancelled:"#dc2626",status_changed:"#f59e0b",updated:"#7c3aed",replied:"#0288d1",bulk_updated:"#ec4899" };
  const recent = [...activity].sort((a,b)=>new Date(b.performedAt).getTime()-new Date(a.performedAt).getTime()).slice(0,limit);
  return (
    <Box sx={{ position:"relative" }}>
      <Box sx={{ position:"absolute",left:13,top:0,bottom:0,width:2,bgcolor:"#f0f0f0",zIndex:0 }} />
      <Stack spacing={0} sx={{ position:"relative",zIndex:1 }}>
        {recent.map((a) => {
          const cfg=TC[a.entityType]??TC.external_task; const ac=AC[a.action]??"#6b7280";
          return (
            <Stack key={a.id} direction="row" spacing={1.5} alignItems="flex-start"
              sx={{ py:1,"&:hover":{ bgcolor:"#f8f9fa" },borderRadius:1,px:0.5 }}>
              <Avatar sx={{ width:28,height:28,bgcolor:`${cfg.color}15`,color:cfg.color,flexShrink:0,zIndex:1,border:"2px solid #fff" }}>{cfg.icon}</Avatar>
              <Box sx={{ flex:1,minWidth:0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" fontWeight={600} noWrap>{a.performedBy}</Typography>
                  <Chip label={a.action.replace("_"," ")} size="small" sx={{ height:16,fontSize:"0.62rem",fontWeight:700,bgcolor:`${ac}18`,color:ac,px:0 }} />
                </Stack>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display:"block",mt:0.1 }}>{a.description}</Typography>
                <Typography variant="caption" color="text.disabled" sx={{ fontSize:"0.65rem" }}>{formatDistanceToNow(new Date(a.performedAt),{ addSuffix:true })}</Typography>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}

const ACTIVITY_TYPE_MAP: Record<string, string> = { Task: "external_task", "Cue Note": "cue_note", Comment: "comment" };

function OverviewTab({ overview, categoryBreakdown, activity, tasks, notes, loading, overviewResolvedSources }: {
  overview: DashboardOverview;
  categoryBreakdown: { categoryId: number; categoryName: string; count: number }[];
  activity: ActivityEntry[];
  tasks: ExternalTask[];
  notes: CueNote[];
  loading?: boolean;
  /** When provided, per-widget skeletons — each widget loads until its source is in this set. */
  overviewResolvedSources?: ReadonlySet<string>;
}) {
  const w = useCW();
  const r = overviewResolvedSources;
  const has = (s: string) => r?.has(s) ?? false;
  const loadingHero0 = r ? !has('external-tasks-counts') : (loading ?? false);
  const loadingHero1 = r ? !has('external-tasks-counts') : (loading ?? false);
  const loadingHero2 = r ? !has('cue-notes-counts') : (loading ?? false);
  const loadingHero3 = r ? !(has('external-tasks-counts') && has('cue-notes-counts') && has('comments-counts')) : (loading ?? false);
  const loadingActivityTrend = r ? !has('activity-trend') : (loading ?? false);
  const loadingTaskStatus = r ? !has('task-status') : (loading ?? false);
  const loadingPriorityQueue = r ? !has('tasks-by-category') : (loading ?? false);
  const loadingCueNoteReply = r ? !has('cue-notes-counts') : (loading ?? false);
  const loadingTasksByCategory = r ? !has('tasks-by-category') : (loading ?? false);
  const [activityTypeFilter, setActivityTypeFilter] = useState<string>("");
  const completionRate = overview.totalExternalTasks>0 ? (overview.completedExternalTasks/overview.totalExternalTasks)*100 : 0;
  const replyRate = overview.totalCueNotes>0 ? (overview.repliedCueNotes/overview.totalCueNotes)*100 : 0;
  const combinedTrend = useMemo(() => {
    const tMap = new Map(overview.taskMonthlyTrend.map((t)=>[t.month,t.count]));
    const cMap = new Map(overview.cueNoteMonthlyTrend.map((t)=>[t.month,t.count]));
    return [...new Set([...tMap.keys(),...cMap.keys()])].map((m)=>({ month:m,tasks:tMap.get(m)??0,notes:cMap.get(m)??0 }));
  },[overview]);
  const catData = useMemo(()=>(categoryBreakdown ?? []).sort((a,b)=>b.count-a.count).slice(0,5).map((c)=>({ name:c.categoryName,count:c.count })),[categoryBreakdown]);
  const filteredActivity = useMemo(()=> 
    activityTypeFilter ? activity.filter((a)=>a.entityType===activityTypeFilter) : activity,
  [activity, activityTypeFilter]);

  const activityTrendCard = loadingActivityTrend ? (
    <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",height:w>=600?310:240,p:2.5 }}>
      <Skeleton variant="text" width="40%" height={24} sx={{ mb:1 }} />
      <Skeleton variant="rectangular" height={w>=600?250:170} sx={{ borderRadius:1 }} />
    </Card>
  ) : (
    <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",height:w>=600?310:240 }}>
      <CardContent sx={{ p:2.5,height:"100%",display:"flex",flexDirection:"column" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box><Typography variant="subtitle1" fontWeight={700}>Activity Trend</Typography><Typography variant="caption" color="text.secondary">Tasks vs Cue Notes — last 6 months</Typography></Box>
          <Stack direction="row" spacing={1.5}>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:"#7c3aed" }} /><Typography variant="caption" color="text.secondary">Tasks</Typography></Stack>
            <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width:10,height:10,borderRadius:"50%",bgcolor:"#00b8d9" }} /><Typography variant="caption" color="text.secondary">Cue Notes</Typography></Stack>
          </Stack>
        </Stack>
        <Box sx={{ flex:1 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={combinedTrend} margin={{ top:5,right:5,bottom:0,left:-20 }}>
              <defs>
                <linearGradient id="ov_gt" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7c3aed" stopOpacity={0.3} /><stop offset="95%" stopColor="#7c3aed" stopOpacity={0.02} /></linearGradient>
                <linearGradient id="ov_gn" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00b8d9" stopOpacity={0.3} /><stop offset="95%" stopColor="#00b8d9" stopOpacity={0.02} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize:11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize:11 }} axisLine={false} tickLine={false} />
              <ReTooltip contentStyle={{ borderRadius:12,fontSize:12,border:"1px solid #e5e7eb" }} />
              <Area type="monotone" dataKey="tasks" name="Tasks" stroke="#7c3aed" strokeWidth={2.5} fill={'url(#ov_gt)'} dot={{ fill:"#7c3aed",r:3 }} />
              <Area type="monotone" dataKey="notes" name="Cue Notes" stroke="#00b8d9" strokeWidth={2.5} fill={'url(#ov_gn)'} dot={{ fill:"#00b8d9",r:3 }} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Stack direction={w>=600?"row":"column"} justifyContent="space-between" alignItems={w>=600?"center":"flex-start"} mb={3} gap={1} flexWrap="wrap">
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color:"#1a1a2e",letterSpacing:"-0.02em",fontSize:w>=600?"1.5rem":"1.15rem" }}>Operations Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">Real-time view of placements, compliance tasks, and team communications</Typography>
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ mb:2.5 }}>
        {[
          { title:"External Tasks",  value:overview.totalExternalTasks, sub:`${overview.openExternalTasks} active · ${overview.cancelledExternalTasks} cancelled`, gradient:"linear-gradient(135deg,#3f2d8f 0%,#7c3aed 100%)", icon:<AssignmentIcon sx={{ color:"#fff",fontSize:22 }} />, trend:overview.externalTasksTrendPercent ?? 0 },
          { title:"Task Completion", value:overview.completedExternalTasks, sub:`${overview.cancelledExternalTasks} cancelled tasks`, gradient:"linear-gradient(135deg,#1b5e20 0%,#16a34a 100%)", icon:<CheckCircleIcon sx={{ color:"#fff",fontSize:22 }} />, ring:completionRate, trend:overview.taskCompletionTrendPercent ?? 0 },
          { title:"Cue Notes",       value:overview.totalCueNotes, sub:`${overview.repliedCueNotes} replied · ${overview.pendingCueNotes} pending`, gradient:"linear-gradient(135deg,#0277bd 0%,#00b8d9 100%)", icon:<NotesIcon sx={{ color:"#fff",fontSize:22 }} />, ring:replyRate, trend:overview.cueNotesTrendPercent ?? 0 },
          { title:"Open Actions",    value:overview.openExternalTasks+overview.pendingCueNotes, sub:`${overview.recentComments} comments this week`, gradient:(overview.openExternalTasks+overview.pendingCueNotes)>20?"linear-gradient(135deg,#b71c1c 0%,#dc2626 100%)":"linear-gradient(135deg,#b45309 0%,#f59e0b 100%)", icon:<PendingActionsIcon sx={{ color:"#fff",fontSize:22 }} />, trend:overview.externalTasksTrendPercent ?? 0 },
        ].map((h,i) => (
          <Grid key={i} item xs={12} sm={6} md={3}><HeroCard {...h} loading={[loadingHero0, loadingHero1, loadingHero2, loadingHero3][i]} /></Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb:2.5 }}>
        <Grid item xs={12} md={7}>
          {activityTrendCard}
        </Grid>
        <Grid item xs={12} md={5}>
          {loadingTaskStatus ? (
            <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",height:w>=600?310:"auto" }}>
              <CardContent sx={{ p:2.5 }}>
                <Skeleton variant="text" width="50%" height={20} sx={{ mb:1 }} />
                <Skeleton variant="rectangular" height={180} sx={{ borderRadius:1 }} />
                <Stack direction="row" flexWrap="wrap" gap={1} justifyContent="center" mt={1}>
                  {[1,2,3,4].map((i) => <Skeleton key={i} variant="rectangular" width={60} height={16} sx={{ borderRadius:1 }} />)}
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",height:w>=600?310:"auto" }}>
              <CardContent sx={{ p:2.5 }}>
                <Box mb={1}><Typography variant="subtitle1" fontWeight={700}>Task Status</Typography><Typography variant="caption" color="text.secondary">Current breakdown by status</Typography></Box>
                <Box sx={{ position:"relative",height:180 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={overview.taskStatusBreakdown} cx="50%" cy="50%" innerRadius={52} outerRadius={82} dataKey="count" nameKey="statusName" paddingAngle={2} isAnimationActive={false}>
                        {overview.taskStatusBreakdown.map((s,i) => <Cell key={i} fill={s.color||CHART_COLORS[i%8]} stroke="none" />)}
                      </Pie>
                      <ReTooltip contentStyle={{ borderRadius:10,fontSize:12,border:"1px solid #e5e7eb" }} formatter={(v,n)=>[v,n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{ position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",pointerEvents:"none" }}>
                    <Typography variant="h5" fontWeight={800} color="#1a1a2e" lineHeight={1}>{overview.totalExternalTasks}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize:"0.62rem" }}>Total</Typography>
                  </Box>
                </Box>
                <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center" mt={0.5}>
                  {overview.taskStatusBreakdown.map((s,i) => (
                    <Stack key={s.statusId} direction="row" spacing={0.5} alignItems="center">
                      <Box sx={{ width:8,height:8,borderRadius:"50%",bgcolor:s.color||CHART_COLORS[i%8] }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize:"0.68rem" }}>{s.statusName} <strong>{s.count}</strong></Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb:2.5 }}>
        <Grid item xs={12} md={5}>
          {loadingPriorityQueue ? (
            <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",minHeight:w>=600?360:260,height:w>=600?360:"auto",p:2.5 }}>
              <CardContent>
                <Skeleton variant="text" width="40%" height={20} sx={{ mb:2 }} />
                <Stack spacing={1.5}>{[1,2,3,4].map((i)=>(<Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius:2 }} />))}</Stack>
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",minHeight:w>=600?360:260,height:w>=600?360:"auto",p:2.5 }}>
              <PriorityQueuePanel tasks={tasks} notes={notes} />
            </Card>
          )}
        </Grid>
        <Grid item xs={12} md={7}>
          <Stack spacing={2} height="100%">
            {(loadingCueNoteReply || loadingTasksByCategory) ? (
              <>
                <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",flex:1 }}><CardContent sx={{ p:2.5 }}>{loadingCueNoteReply ? <><Skeleton variant="text" width="50%" /><Skeleton variant="text" width="30%" height={40} sx={{ mt:1 }} /><Stack spacing={1} mt={2}><Skeleton variant="rectangular" height={24} /><Skeleton variant="rectangular" height={24} /></Stack></> : <><Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}><Box><Typography variant="subtitle1" fontWeight={700}>Cue Note Reply Rate</Typography><Typography variant="caption" color="text.secondary">Response performance</Typography></Box><Typography variant="h4" fontWeight={800} sx={{ color:"#0288d1" }}>{replyRate.toFixed(0)}%</Typography></Stack><Stack spacing={1.5}>{[{ label:"Replied",value:overview.repliedCueNotes,total:overview.totalCueNotes||1,color:"#16a34a" },{ label:"Pending Reply",value:overview.pendingCueNotes,total:overview.totalCueNotes||1,color:"#f59e0b" }].map((item)=>(<Box key={item.label}><Stack direction="row" justifyContent="space-between" mb={0.5}><Typography variant="caption" color="text.secondary" fontWeight={600}>{item.label}</Typography><Typography variant="caption" fontWeight={700}>{item.value.toLocaleString()}</Typography></Stack><LinearProgress variant="determinate" value={(item.value/item.total)*100} sx={{ height:8,borderRadius:4,bgcolor:`${item.color}20`,"& .MuiLinearProgress-bar":{ bgcolor:item.color,borderRadius:4 } }} /></Box>))}</Stack></>}</CardContent></Card>
                <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",flex:1 }}><CardContent sx={{ p:2.5,height:"100%" }}>{loadingTasksByCategory ? <><Typography variant="subtitle1" fontWeight={700} mb={0.25}>Tasks by Category</Typography><Typography variant="caption" color="text.secondary" sx={{ display:"block",mb:1 }}>Compliance workload distribution</Typography><Skeleton variant="rectangular" height={120} sx={{ borderRadius:1,mt:1 }} /></> : <><Typography variant="subtitle1" fontWeight={700} mb={0.25}>Tasks by Category</Typography><Typography variant="caption" color="text.secondary" sx={{ display:"block",mb:1 }}>Compliance workload distribution</Typography><Box sx={{ height:120 }}><ResponsiveContainer width="100%" height="100%"><BarChart data={catData} layout="vertical" margin={{ top:0,right:30,left:0,bottom:0 }}><XAxis type="number" tick={{ fontSize:10 }} axisLine={false} tickLine={false} /><YAxis type="category" dataKey="name" tick={{ fontSize:11 }} axisLine={false} tickLine={false} width={90} /><ReTooltip contentStyle={{ borderRadius:8,fontSize:12 }} /><Bar dataKey="count" name="Tasks" fill="#7c3aed" radius={[0,4,4,0]} maxBarSize={14}>{catData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%8]} />)}</Bar></BarChart></ResponsiveContainer></Box></>}</CardContent></Card>
              </>
            ) : (
              <>
                <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",flex:1 }}>
                  <CardContent sx={{ p:2.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box><Typography variant="subtitle1" fontWeight={700}>Cue Note Reply Rate</Typography><Typography variant="caption" color="text.secondary">Response performance</Typography></Box>
                      <Typography variant="h4" fontWeight={800} sx={{ color:"#0288d1" }}>{replyRate.toFixed(0)}%</Typography>
                    </Stack>
                    <Stack spacing={1.5}>
                      {[
                        { label:"Replied",      value:overview.repliedCueNotes, total:overview.totalCueNotes||1, color:"#16a34a" },
                        { label:"Pending Reply",value:overview.pendingCueNotes, total:overview.totalCueNotes||1, color:"#f59e0b" },
                      ].map((item) => (
                        <Box key={item.label}>
                          <Stack direction="row" justifyContent="space-between" mb={0.5}>
                            <Typography variant="caption" color="text.secondary" fontWeight={600}>{item.label}</Typography>
                            <Typography variant="caption" fontWeight={700}>{item.value.toLocaleString()}</Typography>
                          </Stack>
                          <LinearProgress variant="determinate" value={(item.value/item.total)*100}
                            sx={{ height:8,borderRadius:4,bgcolor:`${item.color}20`,"& .MuiLinearProgress-bar":{ bgcolor:item.color,borderRadius:4 } }} />
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
                <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)",flex:1 }}>
                  <CardContent sx={{ p:2.5,height:"100%" }}>
                    <Typography variant="subtitle1" fontWeight={700} mb={0.25}>Tasks by Category</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display:"block",mb:1 }}>Compliance workload distribution</Typography>
                    <Box sx={{ height:120 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={catData} layout="vertical" margin={{ top:0,right:30,left:0,bottom:0 }}>
                          <XAxis type="number" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize:11 }} axisLine={false} tickLine={false} width={90} />
                          <ReTooltip contentStyle={{ borderRadius:8,fontSize:12 }} />
                          <Bar dataKey="count" name="Tasks" fill="#7c3aed" radius={[0,4,4,0]} maxBarSize={14}>
                            {catData.map((_,i) => <Cell key={i} fill={CHART_COLORS[i%8]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </>
            )}
          </Stack>
        </Grid>
      </Grid>

      {loading ? (
        <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)" }}>
          <CardContent sx={{ p:2.5 }}>
            <Skeleton variant="text" width="35%" height={24} sx={{ mb:2 }} />
            <Stack spacing={1.5}>{[1,2,3,4,5,6].map((i)=>(<Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius:1 }} />))}</Stack>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ borderRadius:3,boxShadow:"0 1px 8px rgba(0,0,0,0.08)" }}>
          <CardContent sx={{ p:2.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Box><Typography variant="subtitle1" fontWeight={700}>Recent Activity</Typography><Typography variant="caption" color="text.secondary">Latest actions across all entities</Typography></Box>
              {w>=600&&<Stack direction="row" spacing={1}>
                {["Task","Cue Note","Comment"].map((t,i) => <Chip key={t} label={t} size="small" sx={{ height:20,fontSize:"0.68rem",fontWeight:600,bgcolor:["#ede9fe","#e0f2fe","#dcfce7"][i],color:["#7c3aed","#0288d1","#16a34a"][i] }} />)}
              </Stack>}
            </Stack>
            <ActivityTimeline activity={activity} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// EXTERNAL TASKS TAB
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_COLOR_MAP: Record<string, string> = {
  "1": "#0288d1", "2": "#f59e0b", "3": "#7c3aed", "4": "#16a34a", "5": "#dc2626", "6": "#6b7280",
};

function buildTasksFetchParams(f: Record<string, unknown>, sm: { field: string; sort: "asc" | "desc" }[]) {
  const statusIds = f.statusIds && typeof f.statusIds === "object" && "value" in f.statusIds && (f.statusIds as { value: string }).value
    ? [Number((f.statusIds as { value: string }).value)] : undefined;
  const search = typeof f.search === "string" && f.search.trim() ? f.search.trim() : undefined;
  const placementId = typeof f.placementId === "string" && f.placementId.trim() ? (Number(f.placementId.trim()) || undefined) : undefined;
  const candidateId = typeof f.candidateId === "string" && f.candidateId.trim() ? (Number(f.candidateId.trim()) || undefined) : undefined;
  const candidateName = typeof f.candidateName === "string" && f.candidateName.trim() ? f.candidateName.trim() : undefined;
  const dr = f.dateRange as { startDate?: string; endDate?: string } | null | undefined;
  const startDate = dr?.startDate ? (typeof dr.startDate === "string" ? dr.startDate.slice(0, 10) : undefined) : undefined;
  const endDate = dr?.endDate ? (typeof dr.endDate === "string" ? dr.endDate.slice(0, 10) : undefined) : undefined;
  const rawField = sm[0]?.field;
  const sortBy = rawField ? (rawField === "updatedAt" || rawField === "createdAt" ? "lastUpdatedDate" : rawField) : "lastUpdatedDate";
  const sortDir = sm[0]?.sort ?? "desc";
  return { statusIds, search, placementId, candidateId, candidateName, startDate, endDate, sortBy, sortDir };
}

/** Map dashboard row to setCurrentExternalTask payload (reuses non-dashboard Tasks flow). */
function mapRowToCurrentTask(row: ExternalTask) {
  return {
    id: row.id,
    placementId: row.placementId,
    candidateId: row.candidateId ?? 0,
    taskNameId: row.taskNameId,
    taskCategoryId: row.taskCategoryId,
    categorySubType1: undefined,
    categorySubType2: undefined,
  };
}

/** Map dashboard row to EditExternalTaskModal editTaskData shape. */
function mapRowToEditTaskData(row: ExternalTask) {
  return {
    taskId: row.id,
    placementId: row.placementId,
    candidateId: row.candidateId,
    taskNameId: row.taskNameId,
    taskCategoryId: row.taskCategoryId,
    task: row.taskName,
    description: row.notes,
    dueDate: row.dueDate,
    assignedTo: [],
    cc: [],
  };
}

function ExternalTasksTab({ tasks, taskStats, tasksTotal, onFetchPage, onUpdate, onDelete, onBulkStatus, onBulkCancel, onCopy, onCreate, loading, gridLoading }: {
  tasks: ExternalTask[];
  taskStats?: TaskStats;
  tasksTotal?: number;
  onFetchPage?: (pageNumber: number, pageSize: number, filters?: { statusIds?: number[]; search?: string; placementId?: number; candidateId?: number; candidateName?: string; startDate?: string; endDate?: string; sortBy?: string; sortDir?: string }) => void | Promise<void>;
  onUpdate: (id: string, data: Partial<ExternalTask>) => void;
  onDelete: (id: string) => void;
  onBulkStatus: (ids: string[], statusId: number) => void;
  onBulkCancel: (placementId: number) => void;
  onCopy: (from: number, to: number) => void;
  onCreate: (data: Partial<ExternalTask>) => void;
  loading?: boolean;
  gridLoading?: boolean;
}) {
  const [sortModel, setSortModel] = useState<{ field: string; sort: "asc" | "desc" }[]>([]);
  const [pageSize, setPageSize] = useState(25);
  const serverMode = !!onFetchPage;
  const formMethods = useForm({ defaultValues: defaultExternalTasksFilterValues, shouldUnregister: false });
  const { handleSubmit, getValues } = formMethods;

  const onApplyFilter = useCallback((values: Record<string, unknown>) => {
    if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildTasksFetchParams(values, sortModel));
  }, [serverMode, onFetchPage, pageSize, sortModel]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [editTask, setEditTask] = useState<ExternalTask|null>(null);
  const [taskForStatusModal, setTaskForStatusModal] = useState<ExternalTask|null>(null);
  const [deleteId, setDeleteId] = useState<string|null>(null);
  const [deleteRow, setDeleteRow] = useState<ExternalTask|null>(null);
  const [cancelPid, setCancelPid] = useState<number|null>(null);
  const [snack, setSnack] = useState<string|null>(null);

  const dispatch = useDispatch();
  const showStatusModal = useSelector(selectShowStatusModal);
  const isExternalTaskUpdated = useSelector(selectedIsExternalTaskUpdated);
  const taskStatusUpdateFlag = useSelector(selectTaskStatusUpdateFlag);

  const filters = formMethods.watch();
  const filtered = useMemo(() => {
    if (serverMode) return tasks;
    let list = [...tasks];
    const q = (filters.search as string)?.toLowerCase?.();
    if (q) list = list.filter((t) => t.taskName.toLowerCase().includes(q) || t.candidateName?.toLowerCase().includes(q) || t.notes?.toLowerCase().includes(q));
    const pid = typeof filters.placementId === "string" && filters.placementId.trim() ? Number(filters.placementId.trim()) : null;
    if (pid != null && !Number.isNaN(pid)) list = list.filter((t) => t.placementId === pid);
    const cid = typeof filters.candidateId === "string" && filters.candidateId.trim() ? Number(filters.candidateId.trim()) : null;
    if (cid != null && !Number.isNaN(cid)) list = list.filter((t) => t.candidateId === cid);
    const cname = (filters.candidateName as string)?.toLowerCase?.();
    if (cname) list = list.filter((t) => t.candidateName?.toLowerCase().includes(cname));
    const sid = filters.statusIds && typeof filters.statusIds === "object" && "value" in filters.statusIds ? Number((filters.statusIds as { value: string }).value) : null;
    if (sid) list = list.filter((t) => t.statusId === sid);
    const dr = filters.dateRange as { startDate?: string; endDate?: string } | null;
    if (dr?.startDate) list = list.filter((t) => t.createdAt >= dr.startDate!);
    if (dr?.endDate) list = list.filter((t) => t.createdAt <= (dr.endDate as string) + "T23:59:59");
    return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [tasks, serverMode, filters]);

  const stats = useMemo(() => taskStats ?? computeTaskStats(tasks), [taskStats, tasks]);
  const totalTasks = serverMode && tasksTotal != null ? tasksTotal : stats.byStatus.reduce((a, s) => a + s.count, 0);

  const columns: GridColDef[] = [
    { field: "taskName", headerName: "Task", flex: 1.5, minWidth: 160, sortable: serverMode,
      renderCell: ({ row }) => <Box><Typography variant="body2" fontWeight={600}>{row.taskName}</Typography><Typography variant="caption" color="text.secondary">{row.taskCategory ?? ""}</Typography></Box> },
    { field: "placementId", headerName: "Placement / Candidate", flex: 1, minWidth: 150, sortable: serverMode,
      renderCell: ({ row }) => <Box><Typography variant="body2" fontWeight={500}>#{row.placementId}</Typography>{row.candidateName && <Typography variant="caption" color="text.secondary">{row.candidateName}</Typography>}</Box> },
    { field: "statusName", headerName: "Status", width: 140, sortable: serverMode,
      renderCell: XGridChipCell({ size: "small", dynamicColor: (p) => STATUS_COLOR_MAP[String((p.row as any).statusId)] ?? "#666" }) },
    { field:"dueDate", headerName:"Due Date", width:120, sortable: serverMode,
      renderCell:({row})=>{ if(!row.dueDate) return <Typography variant="body2" color="text.disabled">—</Typography>; const due=new Date(row.dueDate); const ov=due<new Date()&&![4,5].includes(row.statusId); return <Typography variant="body2" color={ov?"error":"text.primary"} fontWeight={ov?600:400}>{due.toLocaleDateString()}{ov&&" ⚠"}</Typography>; } },
    { field:"notes", headerName:"Notes", flex:1, minWidth:140, sortable:false,
      renderCell:({row})=><Tooltip title={row.notes??""}><Typography variant="body2" noWrap color={row.notes?"text.primary":"text.disabled"}>{row.notes??"—"}</Typography></Tooltip> },
    { field:"actions", headerName:"Actions", width:120, sortable:false, align:"right", headerAlign:"right",
      renderCell:({row})=>(
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          {![4,5].includes(row.statusId)&&<Tooltip title="Complete"><IconButton size="small" sx={{ color:"#16a34a" }} disabled onClick={()=>{
            if (serverMode && dispatch) { dispatch(taskDetailsActions.setIsExternalTask(true)); dispatch(taskDetailsActions.setCurrentExternalTask(mapRowToCurrentTask(row))); dispatch(taskDetailsActions.setTaskStatusUpdateModalId({ taskId: row.id, statusId: row.statusId })); dispatch(taskDetailsActions.setShowTaskStatusModal({ isModalOpen: true, container: "dashboard" })); setTaskForStatusModal(row);
            } else { onUpdate(row.id,{statusId:4,statusName:"Completed"}); setSnack("Marked complete."); }
          }}><CheckCircleIcon sx={{ fontSize:18 }} /></IconButton></Tooltip>}
          <Tooltip title="Edit"><IconButton size="small" sx={{ color:PRIMARY }} disabled onClick={()=>{
            if (serverMode && dispatch) { dispatch(taskDetailsActions.setIsExternalTask(true)); dispatch(taskDetailsActions.setCurrentExternalTask(mapRowToCurrentTask(row))); setEditTask(row);
            } else { setEditTask(row); }
          }}><EditIcon sx={{ fontSize:18 }} /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" sx={{ color:"error.main" }} disabled onClick={()=>{ setDeleteRow(row); setDeleteId(row.id); }}><DeleteIcon sx={{ fontSize:18 }} /></IconButton></Tooltip>
        </Stack>
      )},
  ];

  const w = useCW();
  const gridData = serverMode ? tasks : filtered;
  const refetch = useCallback(() => {
    if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildTasksFetchParams(getValues(), sortModel));
  }, [serverMode, onFetchPage, pageSize, sortModel, getValues]);

  useEffect(() => {
    if (!serverMode || !onFetchPage) return;
    if (isExternalTaskUpdated) {
      refetch();
      dispatch(taskDetailsActions.setIsExternalTaskUpdated(false));
    }
    if (taskStatusUpdateFlag === "SUCCESS" && taskForStatusModal) {
      refetch();
      setTaskForStatusModal(null);
    }
    if (showStatusModal === undefined && taskForStatusModal) {
      setTaskForStatusModal(null);
      refetch();
    }
  }, [isExternalTaskUpdated, taskStatusUpdateFlag, showStatusModal, serverMode, onFetchPage, refetch, dispatch, taskForStatusModal]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onApplyFilter)}>
        <Box>
          <Box sx={{ display:"flex",flexDirection:w>=600?"row":"column",alignItems:w>=600?"center":"flex-start",justifyContent:"space-between",mb:3,gap:1.5 }}>
            <Box><Typography variant="h5" fontWeight={700} sx={{ fontSize:w>=600?"1.5rem":"1.15rem" }}>External Tasks</Typography><Typography variant="body2" color="text.secondary">Manage and action tasks across placements</Typography></Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={()=>setShowCopy(true)} disabled sx={{ textTransform:"none" }}>Copy Tasks</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={()=>setShowCreate(true)} disabled sx={{ textTransform:"none",bgcolor:PRIMARY }}>Create Task</Button>
            </Stack>
          </Box>

          <Grid container spacing={2} sx={{ mb:3 }}>
            {[
              { title:"Total",      value:totalTasks,                   icon:<AssignmentIcon sx={{ color:PRIMARY,fontSize:20 }} />,    iconBg:"#ede7f6" },
              { title:"Completed",  value:stats.tasksCompletedThisMonth, subtitle:"This month", icon:<CheckCircleIcon sx={{ color:"#2e7d32",fontSize:20 }} />, iconBg:"#e8f5e9", color:"#2e7d32" },
              { title:"Overdue",    value:stats.overdueTasks,           icon:<PendingActionsIcon sx={{ color:"#d32f2f",fontSize:20 }} />,iconBg:"#ffebee", color:"#d32f2f" },
              { title:"Categories", value:stats.byCategory.length,      icon:<AssignmentIcon sx={{ color:"#ed6c02",fontSize:20 }} />,  iconBg:"#fff3e0" },
            ].map((s,i) => <Grid key={i} item xs={6} sm={3}><StatCard {...s} loading={loading} /></Grid>)}
          </Grid>

          <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <DashboardExternalTasksFilter onApplyFilter={onApplyFilter} />
            <DashboardExternalTasksChips onApplyFilter={onApplyFilter} />
          </Paper>

          {selectedIds.length>0&&(
            <Box sx={{ px:2,py:1,bgcolor:"#ede7f6",border:"1px solid",borderColor:"divider",borderBottom:"none",borderRadius:"8px 8px 0 0",display:"flex",alignItems:"center",flexWrap:"wrap",gap:1 }}>
              <Chip label={`${selectedIds.length} selected`} size="small" color="primary" onDelete={()=>setSelectedIds([])} />
              <Button size="small" variant="outlined" color="primary" startIcon={<UpdateIcon fontSize="small" />} sx={{ textTransform:"none",height:28 }} onClick={()=>setShowBulkStatus(true)}>Update Status</Button>
              <Button size="small" variant="outlined" color="warning" startIcon={<CancelIcon fontSize="small" />} sx={{ textTransform:"none",height:28 }} onClick={()=>{ const list=serverMode?tasks:filtered; const t=list.find((t)=>selectedIds.includes(t.id)); if(t) setCancelPid(t.placementId); }}>Cancel by Placement</Button>
            </Box>
          )}
          <XGrid
            rows={gridData}
            columns={columns}
            getRowId={(r)=>r.id}
            title="External Tasks"
            loading={gridLoading}
            paginationMode={serverMode ? "server" : "client"}
            sortingMode={serverMode ? "server" : "client"}
            sortModel={sortModel}
            onSortModelChange={(model)=>{ const next=Array.isArray(model)?model:(model as any)?.sortModel??[]; setSortModel(next); if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildTasksFetchParams(getValues(), next)); }}
            rowCount={serverMode ? (tasksTotal ?? 0) : undefined}
            onPageChange={(page)=>{ if (serverMode && onFetchPage) onFetchPage(page + 1, pageSize, buildTasksFetchParams(getValues(), sortModel)); }}
            onPageSizeChange={(size)=>{ setPageSize(size); if (serverMode && onFetchPage) onFetchPage(1, size, buildTasksFetchParams(getValues(), sortModel)); }}
            rowsPerPageOptions={[25,50,100]}
            defaultRowsPerPage={25}
            pagination
            autoHeight
            height={400}
            checkboxSelection
            toolbar
            gridTag="dashboard-external-tasks"
            sx={selectedIds.length>0?DG_SX_BULK:DG_SX}
          />
        </Box>
      </form>

      {/* Create dialog */}
      {showCreate && <TaskFormDlg mode="create" onSave={(d)=>{onCreate(d);setShowCreate(false);setSnack("Task created.");}} onClose={()=>setShowCreate(false)} />}
      {/* Status update modal (Complete) – from dashboard */}
      {showStatusModal?.isModalOpen && showStatusModal?.container === "dashboard" && (
        <ExternalTaskStatusUpdateModal open={true} task={taskForStatusModal} />
      )}
      {/* Edit dialog – use existing EditExternalTaskModal when serverMode, else TaskFormDlg */}
      {editTask && serverMode ? (
        <EditExternalTaskModal
          open
          handleClose={()=>{ setEditTask(null); dispatch(taskDetailsActions.setIsExternalTaskUpdated(false)); }}
          editTaskData={mapRowToEditTaskData(editTask)}
          container={editTask.placementId}
          taskDetail={false}
        />
      ) : editTask ? (
        <TaskFormDlg mode="edit" task={editTask} onSave={(d)=>{onUpdate(editTask.id,d);setEditTask(null);setSnack("Task updated.");}} onClose={()=>setEditTask(null)} />
      ) : null}
      {/* Bulk status */}
      {showBulkStatus && <BulkStatusDlg onApply={(sid)=>{onBulkStatus(selectedIds as string[],sid);setShowBulkStatus(false);setSelectedIds([]);setSnack("Status updated.");}} onClose={()=>setShowBulkStatus(false)} />}
      {/* Copy */}
      {showCopy && <CopyTasksDlg onCopy={(from,to)=>{onCopy(from,to);setShowCopy(false);setSnack("Tasks copied.");}} onClose={()=>setShowCopy(false)} />}
      {/* Delete */}
      <ConfirmDlg open={!!deleteId} title="Delete Task" message="Are you sure? This cannot be undone." confirmLabel="Delete" severity="error"
        onConfirm={()=>{
          if (serverMode && deleteRow) {
            dispatch(taskDetailsActions.setCurrentExternalTask(mapRowToCurrentTask(deleteRow)));
            dispatch(taskDetailsActions.setIsExternalTask(true));
            dispatch(taskDetailsActions.updateExternalTaskStatus({ update: { statusId: TaskStatus.Cancelled, statusUpdateReason: 'Deleted' }, initialStatus: deleteRow.statusId }));
            setDeleteRow(null);
            setDeleteId(null);
            setSnack("Task deleted.");
          } else {
            onDelete(deleteId!);
            setDeleteId(null);
            setSnack("Task deleted.");
          }
        }} onClose={()=>{ setDeleteId(null); setDeleteRow(null); }} />
      {/* Bulk cancel by placement */}
      <ConfirmDlg open={!!cancelPid} title="Cancel Tasks by Placement" message={`Cancel ALL tasks for placement #${cancelPid}?`} confirmLabel="Cancel Tasks" severity="warning"
        onConfirm={()=>{onBulkCancel(cancelPid!);setCancelPid(null);setSnack("Tasks cancelled.");}} onClose={()=>setCancelPid(null)} />
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={()=>setSnack(null)} message={snack} />
    </FormProvider>
  );
}

function TaskFormDlg({ mode, task, onSave, onClose }: { mode:"create"|"edit"; task?: ExternalTask; onSave:(d:Partial<ExternalTask>)=>void; onClose:()=>void }) {
  const [f, setF] = useState({ placementId:task?.placementId??"", candidateName:task?.candidateName??"", taskName:task?.taskName??"", taskCategory:task?.taskCategory??"", statusId:task?.statusId??1, dueDate:task?.dueDate??"", notes:task?.notes??"" });
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>{mode==="create"?"Create Task":"Edit Task"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt:0.5 }}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Placement ID" value={f.placementId} onChange={(e)=>setF({...f,placementId:e.target.value})} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Candidate Name" value={f.candidateName} onChange={(e)=>setF({...f,candidateName:e.target.value})} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Task Name" value={f.taskName} onChange={(e)=>setF({...f,taskName:e.target.value})} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Category" value={f.taskCategory} onChange={(e)=>setF({...f,taskCategory:e.target.value})} size="small" /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Status" value={f.statusId} onChange={(e)=>setF({...f,statusId:Number(e.target.value)})} size="small">
              {[1,2,3,4,5,6].map((s)=><MenuItem key={s} value={s}>{STATUS_MAP[s]}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Due Date" type="date" value={f.dueDate} onChange={(e)=>setF({...f,dueDate:e.target.value})} size="small" InputLabelProps={{ shrink:true }} /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Notes" value={f.notes} onChange={(e)=>setF({...f,notes:e.target.value})} size="small" multiline rows={2} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px:3,pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button variant="contained" sx={{ textTransform:"none",bgcolor:PRIMARY }} onClick={()=>onSave({ placementId:Number(f.placementId),candidateName:f.candidateName,taskName:f.taskName,taskCategory:f.taskCategory,statusId:f.statusId,statusName:STATUS_MAP[f.statusId],dueDate:f.dueDate||undefined,notes:f.notes })}>
          {mode==="create"?"Create":"Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function BulkStatusDlg({ onApply, onClose }: { onApply:(s:number)=>void; onClose:()=>void }) {
  const [sid, setSid] = useState(4);
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>Update Status</DialogTitle>
      <DialogContent><TextField select fullWidth label="New Status" value={sid} onChange={(e)=>setSid(Number(e.target.value))} size="small" sx={{ mt:1 }}>{[1,2,3,4,5,6].map((s)=><MenuItem key={s} value={s}>{STATUS_MAP[s]}</MenuItem>)}</TextField></DialogContent>
      <DialogActions sx={{ px:3,pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button variant="contained" sx={{ textTransform:"none",bgcolor:PRIMARY }} onClick={()=>onApply(sid)}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
}
function CopyTasksDlg({ onCopy, onClose }: { onCopy:(f:number,t:number)=>void; onClose:()=>void }) {
  const [f, setF] = useState({ from:"",to:"" });
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>Copy Tasks</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt:0.5 }}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="From Placement ID" value={f.from} onChange={(e)=>setF({...f,from:e.target.value})} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="To Placement ID" value={f.to} onChange={(e)=>setF({...f,to:e.target.value})} size="small" /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px:3,pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button variant="contained" sx={{ textTransform:"none",bgcolor:PRIMARY }} onClick={()=>onCopy(Number(f.from),Number(f.to))}>Copy</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CUE NOTES TAB
// ─────────────────────────────────────────────────────────────────────────────

function buildCueNotesFetchParams(f: Record<string, unknown>, sm: { field: string; sort: "asc" | "desc" }[]) {
  const repliedVal = f.replied && typeof f.replied === "object" && "value" in f.replied ? (f.replied as { value: string }).value : undefined;
  const isReplied = repliedVal === "true" ? true : repliedVal === "false" ? false : undefined;
  const search = typeof f.search === "string" && f.search.trim() ? f.search.trim() : undefined;
  const placementIdStr = typeof f.placementId === "string" && f.placementId.trim() ? f.placementId.trim() : undefined;
  const placementId = placementIdStr ? parseInt(placementIdStr, 10) : undefined;
  const dr = f.dateRange as { startDate?: string; endDate?: string } | null | undefined;
  const startDate = dr?.startDate ? (typeof dr.startDate === "string" ? dr.startDate.slice(0, 10) : undefined) : undefined;
  const endDate = dr?.endDate ? (typeof dr.endDate === "string" ? dr.endDate.slice(0, 10) : undefined) : undefined;
  const sortBy = sm[0]?.field ? (sm[0].field === "createdAt" ? "sentDate" : sm[0].field) : "sentDate";
  const sortDir = sm[0]?.sort ?? "desc";
  return { isReplied, search, placementId: !Number.isNaN(placementId) && placementId != null ? placementId : undefined, startDate, endDate, sortBy, sortDir };
}

function CueNotesTab({ notes, cueNoteStats, cueNotesTotal, onFetchPage, onMarkReplied, onCreate, onUpdate, onDelete, onBulkCreate, onBulkCancel, loading, gridLoading }: {
  notes: CueNote[];
  cueNoteStats?: CueNoteStats;
  cueNotesTotal?: number;
  onFetchPage?: (pageNumber: number, pageSize: number, filters?: { isReplied?: boolean; search?: string; placementId?: number; startDate?: string; endDate?: string; sortBy?: string; sortDir?: string }) => void | Promise<void>;
  onMarkReplied:(id:number,text:string,by:string)=>void;
  onCreate:(d:Partial<CueNote>)=>void;
  onUpdate:(id:number,d:Partial<CueNote>)=>void;
  onDelete:(id:number)=>void;
  onBulkCreate:(pids:number[],text:string)=>void;
  onBulkCancel:(ids:number[])=>void;
  loading?: boolean;
  gridLoading?: boolean;
}) {
  const [sortModel, setSortModel] = useState<{ field: string; sort: "asc" | "desc" }[]>([]);
  const [pageSize, setPageSize] = useState(25);
  const serverMode = !!onFetchPage;
  const formMethods = useForm({ defaultValues: defaultCueNotesFilterValues, shouldUnregister: false });
  const { handleSubmit, getValues } = formMethods;

  const onApplyFilter = useCallback((values: Record<string, unknown>) => {
    if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildCueNotesFetchParams(values, sortModel));
  }, [serverMode, onFetchPage, pageSize, sortModel]);
  const [selectedIds,setSelectedIds]=useState<number[]>([]);
  const [showCreate,setShowCreate]=useState(false);
  const [showBulkCreate,setShowBulkCreate]=useState(false);
  const [replyNote,setReplyNote]=useState<CueNote|null>(null);
  const [editNote,setEditNote]=useState<CueNote|null>(null);
  const [deleteId,setDeleteId]=useState<number|null>(null);
  const [cancelConfirm,setCancelConfirm]=useState(false);
  const [snack,setSnack]=useState<string|null>(null);

  const filters = formMethods.watch();
  const filtered = useMemo(() => {
    if (serverMode) return notes;
    let list = [...notes];
    const q = (filters.search as string)?.toLowerCase?.();
    if (q) list = list.filter((n) => n.text.toLowerCase().includes(q) || n.senderName.toLowerCase().includes(q));
    const rv = filters.replied && typeof filters.replied === "object" && "value" in filters.replied ? (filters.replied as { value: string }).value : undefined;
    if (rv === "true") list = list.filter((n) => n.isReplied);
    if (rv === "false") list = list.filter((n) => !n.isReplied);
    return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [notes, serverMode, filters]);

  const stats = useMemo(() => cueNoteStats ?? computeCueNoteStats(notes), [cueNoteStats, notes]);
  // When API returns total=-1 (sentinel from @SkipCount), use widget counts based on current filter
  const effectiveRowCount = useMemo(() => {
    if (cueNotesTotal == null || cueNotesTotal >= 0) return cueNotesTotal ?? 0;
    const rv = filters.replied && typeof filters.replied === 'object' && 'value' in filters.replied ? (filters.replied as { value: string }).value : undefined;
    if (rv === 'true') return stats.replied;
    if (rv === 'false') return stats.pending;
    return stats.total;
  }, [cueNotesTotal, filters.replied, stats.replied, stats.pending, stats.total]);

  const columns: GridColDef[] = [
    { field:"placementId", headerName:"Placement / Sender", flex:1, minWidth:150,
      renderCell:({row})=><Box><Typography variant="body2" fontWeight={600}>#{row.placementId}</Typography><Typography variant="caption" color="text.secondary">{row.senderName}</Typography></Box> },
    { field:"text", headerName:"Message", flex:2, minWidth:200, sortable:false,
      renderCell:({row})=><Tooltip title={row.text}><Typography variant="body2" noWrap>{row.text}</Typography></Tooltip> },
    { field:"isReplied", headerName:"Status", width:160, sortable:false,
      renderCell: ({ row }) => {
        const r = row as CueNote;
        const label = r.isReplied ? "Replied" : "Pending Reply";
        const color = r.isReplied ? "#2e7d32" : "#ed6c02";
        return <Chip size="small" label={label} sx={{ color, backgroundColor: `${color}20`, border: `1px solid ${color}` }} />;
      } },
    { field:"dueDate", headerName:"Due", width:110,
      renderCell:({row})=>{ if(!row.dueDate) return <Typography variant="body2" color="text.disabled">—</Typography>; const d=new Date(row.dueDate); const ov=!row.isReplied&&d<new Date(); return <Typography variant="body2" color={ov?"error":"text.primary"} fontWeight={ov?600:400}>{d.toLocaleDateString()}{ov&&" ⚠"}</Typography>; } },
    { field:"createdAt", headerName:"Created", width:110,
      renderCell:({row})=><Typography variant="body2">{new Date(row.createdAt).toLocaleDateString()}</Typography> },
    { field:"actions", headerName:"Actions", width:180, sortable:false, align:"right", headerAlign:"right",
      renderCell:({row})=>(
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
          {!row.isReplied&&<Button size="small" variant="contained" color="success" disabled startIcon={<DoneAllIcon sx={{ fontSize:14 }} />} onClick={()=>setReplyNote(row)} sx={{ fontSize:"0.72rem",textTransform:"none",py:0.3,px:1,minWidth:0 }}>Mark Replied</Button>}
          <Tooltip title="Edit"><IconButton size="small" sx={{ color:PRIMARY }} disabled onClick={()=>setEditNote(row)}><EditIcon sx={{ fontSize:18 }} /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" sx={{ color:"error.main" }} disabled onClick={()=>setDeleteId(row.taskId)}><DeleteIcon sx={{ fontSize:18 }} /></IconButton></Tooltip>
        </Stack>
      )},
  ];

  const w = useCW();
  const gridData = serverMode ? notes : filtered;
  const refetch = useCallback(() => {
    if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildCueNotesFetchParams(getValues(), sortModel));
  }, [serverMode, onFetchPage, pageSize, sortModel, getValues]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onApplyFilter)}>
        <Box>
          <Box sx={{ display:"flex",flexDirection:w>=600?"row":"column",alignItems:w>=600?"center":"flex-start",justifyContent:"space-between",mb:3,gap:1.5 }}>
            <Box><Typography variant="h5" fontWeight={700} sx={{ fontSize:w>=600?"1.5rem":"1.15rem" }}>Cue Notes</Typography><Typography variant="body2" color="text.secondary">Reply, edit, and manage cue notes across placements</Typography></Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button variant="outlined" startIcon={<GroupAddIcon />} onClick={()=>setShowBulkCreate(true)} disabled sx={{ textTransform:"none" }}>Bulk Create</Button>
              <Button variant="contained" startIcon={<AddIcon />} onClick={()=>setShowCreate(true)} disabled sx={{ textTransform:"none",bgcolor:PRIMARY }}>Create Cue Note</Button>
            </Stack>
          </Box>
          <Grid container spacing={2} sx={{ mb:3 }}>
            {[
              { title:"Total",        value:stats.total,            icon:<NotesIcon sx={{ color:"#0288d1",fontSize:20 }} />,     iconBg:"#e3f2fd" },
              { title:"Replied",      value:stats.replied,          icon:<ReplyIcon sx={{ color:"#2e7d32",fontSize:20 }} />,     iconBg:"#e8f5e9", color:"#2e7d32" },
              { title:"Pending Reply",value:stats.pending,          icon:<PendingActionsIcon sx={{ color:"#ed6c02",fontSize:20 }} />,iconBg:"#fff3e0", color:"#ed6c02" },
              { title:"Reply Rate",   value:`${stats.replyRate.toFixed(1)}%`, icon:<CheckCircleIcon sx={{ color:PRIMARY,fontSize:20 }} />, iconBg:"#ede7f6" },
            ].map((s,i)=><Grid key={i} item xs={6} sm={3}><StatCard {...s} loading={loading} /></Grid>)}
          </Grid>
          <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <DashboardCueNotesFilter
              onApplyFilter={onApplyFilter}
              repliedCount={stats.replied}
              pendingCount={stats.pending}
            />
            <DashboardCueNotesChips onApplyFilter={onApplyFilter} />
          </Paper>
          {selectedIds.length>0&&(
            <Box sx={{ px:2,py:1,bgcolor:"#ede7f6",border:"1px solid",borderColor:"divider",borderBottom:"none",borderRadius:"8px 8px 0 0",display:"flex",alignItems:"center",flexWrap:"wrap",gap:1 }}>
              <Chip label={`${selectedIds.length} selected`} size="small" color="primary" onDelete={()=>setSelectedIds([])} />
              <Button size="small" variant="outlined" color="warning" startIcon={<CancelIcon fontSize="small" />} sx={{ textTransform:"none",height:28 }} onClick={()=>setCancelConfirm(true)}>Cancel Selected</Button>
            </Box>
          )}
          <XGrid
            rows={gridData}
            columns={columns}
            getRowId={(r)=>(r as CueNote).id ?? `cn-${(r as CueNote).taskId}`}
            title="Cue Notes"
            loading={gridLoading}
            paginationMode={serverMode ? "server" : "client"}
            sortingMode={serverMode ? "server" : "client"}
            sortModel={sortModel}
            onSortModelChange={(model)=>{ const next=Array.isArray(model)?model:(model as any)?.sortModel??[]; setSortModel(next); if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildCueNotesFetchParams(getValues(), next)); }}
            rowCount={serverMode ? effectiveRowCount : undefined}
            onPageChange={(page)=>{ if (serverMode && onFetchPage) onFetchPage(page + 1, pageSize, buildCueNotesFetchParams(getValues(), sortModel)); }}
            onPageSizeChange={(size)=>{ setPageSize(size); if (serverMode && onFetchPage) onFetchPage(1, size, buildCueNotesFetchParams(getValues(), sortModel)); }}
            rowsPerPageOptions={[25,50,100]}
            defaultRowsPerPage={25}
            pagination
            autoHeight
            height={400}
            hideFooterRowCount={false}
            paginationProps={{ hidePageCount: false }}
            checkboxSelection
            toolbar
            gridTag="dashboard-cue-notes"
            sx={selectedIds.length>0?DG_SX_BULK:DG_SX}
          />
        </Box>
      </form>
      {replyNote&&<MarkRepliedDlg note={replyNote} onSave={(txt,by)=>{onMarkReplied(replyNote.taskId,txt,by);setReplyNote(null);setSnack("Marked as replied.");}} onClose={()=>setReplyNote(null)} />}
      {showCreate&&<CueNoteFormDlg mode="create" onSave={(d)=>{onCreate(d);setShowCreate(false);setSnack("Cue note created.");}} onClose={()=>setShowCreate(false)} />}
      {editNote&&<CueNoteFormDlg mode="edit" note={editNote} onSave={(d)=>{onUpdate(editNote.taskId,d);setEditNote(null);setSnack("Cue note updated.");}} onClose={()=>setEditNote(null)} />}
      {showBulkCreate&&<BulkCreateNotesDlg onSave={(pids,txt)=>{onBulkCreate(pids,txt);setShowBulkCreate(false);setSnack(`${pids.length} cue notes created.`);}} onClose={()=>setShowBulkCreate(false)} />}
      <ConfirmDlg open={!!deleteId} title="Delete Cue Note" message="Delete this cue note? This cannot be undone." confirmLabel="Delete" severity="error"
        onConfirm={()=>{onDelete(deleteId!);setDeleteId(null);setSnack("Deleted.");}} onClose={()=>setDeleteId(null)} />
      <ConfirmDlg open={cancelConfirm} title="Bulk Cancel Cue Notes" message={`Cancel ${selectedIds.length} selected cue note(s)?`} confirmLabel="Cancel Notes" severity="warning"
        onConfirm={()=>{onBulkCancel(selectedIds as number[]);setCancelConfirm(false);setSelectedIds([]);setSnack(`${selectedIds.length} cancelled.`);}} onClose={()=>setCancelConfirm(false)} />
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={()=>setSnack(null)} message={snack} />
    </FormProvider>
  );
}

function MarkRepliedDlg({ note, onSave, onClose }: { note:CueNote; onSave:(txt:string,by:string)=>void; onClose:()=>void }) {
  const [txt,setTxt]=useState(""); const [by,setBy]=useState("");
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>Mark as Replied</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb:1.5 }}>{note.text.slice(0,100)}{note.text.length>100?"…":""}</Typography>
        <TextField fullWidth label="Replied By" value={by} onChange={(e)=>setBy(e.target.value)} size="small" sx={{ mb:1.5 }} />
        <TextField fullWidth label="Reply Text (optional)" value={txt} onChange={(e)=>setTxt(e.target.value)} size="small" multiline rows={2} />
      </DialogContent>
      <DialogActions sx={{ px:3,pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button variant="contained" color="success" sx={{ textTransform:"none" }} onClick={()=>onSave(txt,by||"System")}>Confirm Replied</Button>
      </DialogActions>
    </Dialog>
  );
}
function CueNoteFormDlg({ mode, note, onSave, onClose }: { mode:"create"|"edit"; note?: CueNote; onSave:(d:Partial<CueNote>)=>void; onClose:()=>void }) {
  const [f,setF]=useState({ placementId:note?.placementId??"", senderName:note?.senderName??"", text:note?.text??"", dueDate:note?.dueDate??"" });
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>{mode==="create"?"Create Cue Note":"Edit Cue Note"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt:0.5 }}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Placement ID" value={f.placementId} onChange={(e)=>setF({...f,placementId:e.target.value})} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Sender Name" value={f.senderName} onChange={(e)=>setF({...f,senderName:e.target.value})} size="small" /></Grid>
          <Grid item xs={12}><TextField fullWidth label="Message" value={f.text} onChange={(e)=>setF({...f,text:e.target.value})} size="small" multiline rows={2} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Due Date" type="date" value={f.dueDate} onChange={(e)=>setF({...f,dueDate:e.target.value})} size="small" InputLabelProps={{ shrink:true }} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px:3,pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button variant="contained" sx={{ textTransform:"none",bgcolor:PRIMARY }} onClick={()=>onSave({ placementId:Number(f.placementId),senderName:f.senderName,text:f.text,dueDate:f.dueDate||undefined })}>
          {mode==="create"?"Create":"Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
function BulkCreateNotesDlg({ onSave, onClose }: { onSave:(pids:number[],txt:string)=>void; onClose:()=>void }) {
  const [pids,setPids]=useState(""); const [txt,setTxt]=useState("");
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>Bulk Create Cue Notes</DialogTitle>
      <DialogContent>
        <TextField fullWidth label="Placement IDs (comma separated)" value={pids} onChange={(e)=>setPids(e.target.value)} size="small" sx={{ mt:1,mb:2 }} />
        <TextField fullWidth label="Message" value={txt} onChange={(e)=>setTxt(e.target.value)} size="small" multiline rows={3} />
      </DialogContent>
      <DialogActions sx={{ px:3,pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button variant="contained" sx={{ textTransform:"none",bgcolor:PRIMARY }} onClick={()=>onSave(pids.split(",").map((s)=>Number(s.trim())).filter(Boolean),txt)}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// COMMENTS TAB
// ─────────────────────────────────────────────────────────────────────────────

function buildCommentsFetchParams(f: Record<string, unknown>, sm: { field: string; sort: "asc" | "desc" }[]) {
  const search = typeof f.search === "string" && f.search.trim() ? f.search.trim() : undefined;
  const placementIdRaw = f.placementId;
  const placementId = typeof placementIdRaw === "string" && placementIdRaw.trim() ? parseInt(placementIdRaw.trim(), 10) : typeof placementIdRaw === "number" ? placementIdRaw : undefined;
  const taskIdRaw = f.taskId;
  const taskId = typeof taskIdRaw === "string" && taskIdRaw.trim() ? parseInt(taskIdRaw.trim(), 10) : typeof taskIdRaw === "number" ? taskIdRaw : undefined;
  const rootTaskIdRaw = f.rootTaskId;
  const rootTaskId = typeof rootTaskIdRaw === "string" && rootTaskIdRaw.trim() ? parseInt(rootTaskIdRaw.trim(), 10) : typeof rootTaskIdRaw === "number" ? rootTaskIdRaw : undefined;
  const entityTypeVal = f.entityType && typeof f.entityType === "object" && "value" in f.entityType ? (f.entityType as { value: string }).value : undefined;
  const entityType = entityTypeVal && String(entityTypeVal).trim() ? entityTypeVal.trim() : undefined;
  const dr = f.dateRange as { startDate?: string; endDate?: string } | null | undefined;
  const startDate = dr?.startDate ? (typeof dr.startDate === "string" ? dr.startDate.slice(0, 10) : undefined) : undefined;
  const endDate = dr?.endDate ? (typeof dr.endDate === "string" ? dr.endDate.slice(0, 10) : undefined) : undefined;
  const sortBy = sm[0]?.field ? (sm[0].field === "createdAt" ? "sentDate" : sm[0].field) : "sentDate";
  const sortDir = sm[0]?.sort ?? "desc";
  return { search, placementId: isNaN(placementId as number) ? undefined : placementId, taskId: isNaN(taskId as number) ? undefined : taskId, rootTaskId: isNaN(rootTaskId as number) ? undefined : rootTaskId, entityType, startDate, endDate, sortBy, sortDir };
}

function CommentsTab({ comments, commentStats, commentsTotal, onFetchPage, onCreate, onUpdate, onDelete, onBulkDelete, loading, gridLoading }: {
  comments: Comment[];
  commentStats?: CommentStats;
  commentsTotal?: number;
  onFetchPage?: (pageNumber: number, pageSize: number, filters?: { search?: string; placementId?: number; taskId?: number; rootTaskId?: number; entityType?: string; startDate?: string; endDate?: string; sortBy?: string; sortDir?: string }) => void | Promise<void>;
  onCreate:(d:Partial<Comment>)=>void;
  onUpdate:(id:number,text:string)=>void;
  onDelete:(id:number)=>void;
  onBulkDelete:(ids:number[])=>void;
  loading?: boolean;
  gridLoading?: boolean;
}) {
  const [sortModel, setSortModel] = useState<{ field: string; sort: "asc" | "desc" }[]>([]);
  const [pageSize, setPageSize] = useState(25);
  const serverMode = !!onFetchPage;
  const formMethods = useForm({ defaultValues: defaultCommentsFilterValues, shouldUnregister: false });
  const { handleSubmit, getValues } = formMethods;

  const onApplyFilter = useCallback((values: Record<string, unknown>) => {
    if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildCommentsFetchParams(values, sortModel));
  }, [serverMode, onFetchPage, pageSize, sortModel]);
  const [selectedIds,setSelectedIds]=useState<number[]>([]);
  const [showCreate,setShowCreate]=useState(false);
  const [editComment,setEditComment]=useState<Comment|null>(null);
  const [deleteId,setDeleteId]=useState<number|null>(null);
  const [bulkDel,setBulkDel]=useState(false);
  const [snack,setSnack]=useState<string|null>(null);

  const filters = formMethods.watch();
  const filtered = useMemo(() => {
    if (serverMode) return comments;
    let list = [...comments];
    const q = (filters.search as string)?.toLowerCase?.();
    if (q) list = list.filter((c) => c.text.toLowerCase().includes(q) || c.authorName.toLowerCase().includes(q));
    return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [comments, serverMode, filters]);

  const stats = useMemo(() => commentStats ?? computeCommentStats(comments), [commentStats, comments]);
  // When API returns total=-1 (sentinel from @SkipCount), use widget count
  const effectiveRowCount = useMemo(() => {
    if (commentsTotal == null || commentsTotal >= 0) return commentsTotal ?? 0;
    return stats.total;
  }, [commentsTotal, stats.total]);

  const columns: GridColDef[] = [
    { field:"authorName", headerName:"Author", width:170,
      renderCell:({row})=>(
        <Stack direction="row" spacing={1} alignItems="center">
          <Avatar sx={{ width:28,height:28,bgcolor:`${PRIMARY}18`,color:PRIMARY,fontSize:"0.75rem",fontWeight:700 }}>{row.authorName.split(" ").map((n:string)=>n[0]).join("").slice(0,2)}</Avatar>
          <Typography variant="body2" fontWeight={600}>{row.authorName}</Typography>
        </Stack>
      )},
    { field:"text", headerName:"Comment", flex:2, minWidth:200, sortable:false,
      renderCell:({row})=>(
        <Box>
          <Typography variant="body2" noWrap>{row.text}</Typography>
          {row.isEdited&&<Chip label="Edited" size="small" sx={{ height:16,fontSize:"0.6rem",mt:0.25,bgcolor:"#f3f4f6" }} />}
        </Box>
      )},
    { field:"entityType", headerName:"Entity", width:150, sortable:false,
      renderCell:({row})=><Typography variant="body2" color="text.secondary" sx={{ textTransform:"capitalize" }}>{row.entityType?.replace("_"," ")??""}</Typography> },
    { field:"createdAt", headerName:"Created", width:110,
      renderCell:({row})=><Typography variant="body2">{new Date(row.createdAt).toLocaleDateString()}</Typography> },
    { field:"actions", headerName:"Actions", width:100, sortable:false, align:"right", headerAlign:"right",
      renderCell:({row})=>(
        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
          <Tooltip title="Edit"><IconButton size="small" sx={{ color:PRIMARY }} disabled onClick={()=>setEditComment(row)}><EditIcon sx={{ fontSize:18 }} /></IconButton></Tooltip>
          <Tooltip title="Delete"><IconButton size="small" sx={{ color:"error.main" }} disabled onClick={()=>setDeleteId(row.taskId)}><DeleteIcon sx={{ fontSize:18 }} /></IconButton></Tooltip>
        </Stack>
      )},
  ];

  const w = useCW();
  const gridData = serverMode ? comments : filtered;
  const refetch = useCallback(() => {
    if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildCommentsFetchParams(getValues(), sortModel));
  }, [serverMode, onFetchPage, pageSize, sortModel, getValues]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onApplyFilter)}>
        <Box>
          <Box sx={{ display:"flex",flexDirection:w>=600?"row":"column",alignItems:w>=600?"center":"flex-start",justifyContent:"space-between",mb:3,gap:1.5 }}>
            <Box><Typography variant="h5" fontWeight={700} sx={{ fontSize:w>=600?"1.5rem":"1.15rem" }}>Comments</Typography><Typography variant="body2" color="text.secondary">View, edit, and manage comments across all entities</Typography></Box>
            <Button variant="contained" startIcon={<AddIcon />} onClick={()=>setShowCreate(true)} disabled sx={{ textTransform:"none",bgcolor:PRIMARY }}>Add Comment</Button>
          </Box>
          <Grid container spacing={2} sx={{ mb:3 }}>
            {[
              { title:"Total Comments",value:stats.total,             icon:<CommentIcon sx={{ color:"#ed6c02",fontSize:20 }} />,iconBg:"#fff3e0" },
              { title:"This Month",    value:stats.createdThisMonth,  icon:<CommentIcon sx={{ color:"#0288d1",fontSize:20 }} />,iconBg:"#e3f2fd",color:"#0288d1" },
              { title:"This Week",     value:stats.createdThisWeek,   icon:<CommentIcon sx={{ color:"#2e7d32",fontSize:20 }} />,iconBg:"#e8f5e9",color:"#2e7d32" },
              { title:"Top Commenters",value:stats.topCommenters.length, icon:<PersonIcon sx={{ color:PRIMARY,fontSize:20 }} />,iconBg:"#ede7f6" },
            ].map((s,i)=><Grid key={i} item xs={6} sm={3}><StatCard {...s} loading={loading} /></Grid>)}
          </Grid>
          <Paper sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <DashboardCommentsFilter onApplyFilter={onApplyFilter} />
            <DashboardCommentsChips onApplyFilter={onApplyFilter} />
          </Paper>
          {selectedIds.length>0&&(
            <Box sx={{ px:2,py:1,bgcolor:"#ffebee",border:"1px solid",borderColor:"divider",borderBottom:"none",borderRadius:"8px 8px 0 0",display:"flex",alignItems:"center",flexWrap:"wrap",gap:1 }}>
              <Chip label={`${selectedIds.length} selected`} size="small" color="error" onDelete={()=>setSelectedIds([])} />
              <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon fontSize="small" />} sx={{ textTransform:"none",height:28 }} onClick={()=>setBulkDel(true)}>Delete Selected</Button>
            </Box>
          )}
          <XGrid
            rows={gridData}
            columns={columns}
            getRowId={(r)=>`comment-${(r as Comment).rootTaskId ?? 0}-${(r as Comment).taskId ?? 0}`}
            title="Comments"
            loading={gridLoading}
            paginationMode={serverMode ? "server" : "client"}
            sortingMode={serverMode ? "server" : "client"}
            sortModel={sortModel}
            onSortModelChange={(model)=>{ const next=Array.isArray(model)?model:(model as any)?.sortModel??[]; setSortModel(next); if (serverMode && onFetchPage) onFetchPage(1, pageSize, buildCommentsFetchParams(getValues(), next)); }}
            rowCount={serverMode ? effectiveRowCount : undefined}
            onPageChange={(page)=>{ if (serverMode && onFetchPage) onFetchPage(page + 1, pageSize, buildCommentsFetchParams(getValues(), sortModel)); }}
            onPageSizeChange={(size)=>{ setPageSize(size); if (serverMode && onFetchPage) onFetchPage(1, size, buildCommentsFetchParams(getValues(), sortModel)); }}
            rowsPerPageOptions={[25,50,100]}
            defaultRowsPerPage={25}
            pagination
            autoHeight
            height={400}
            hideFooterRowCount={false}
            paginationProps={{ hidePageCount: false }}
            checkboxSelection
            toolbar
            gridTag="dashboard-comments"
            sx={selectedIds.length>0?DG_SX_BULK:DG_SX}
          />
        </Box>
      </form>
      {showCreate&&<CommentFormDlg mode="create" onSave={(d)=>{onCreate(d);setShowCreate(false);setSnack("Comment added.");}} onClose={()=>setShowCreate(false)} />}
      {editComment&&<CommentFormDlg mode="edit" comment={editComment} onSave={(d)=>{onUpdate(editComment.taskId,d.text??editComment.text);setEditComment(null);setSnack("Comment updated.");}} onClose={()=>setEditComment(null)} />}
      <ConfirmDlg open={!!deleteId} title="Delete Comment" message="Delete this comment? This cannot be undone." confirmLabel="Delete" severity="error"
        onConfirm={()=>{onDelete(deleteId!);setDeleteId(null);setSnack("Comment deleted.");}} onClose={()=>setDeleteId(null)} />
      <ConfirmDlg open={bulkDel} title="Bulk Delete Comments" message={`Permanently delete ${selectedIds.length} selected comment(s)?`} confirmLabel="Delete All" severity="error"
        onConfirm={()=>{onBulkDelete(selectedIds as number[]);setBulkDel(false);setSelectedIds([]);setSnack(`${selectedIds.length} deleted.`);}} onClose={()=>setBulkDel(false)} />
      <Snackbar open={!!snack} autoHideDuration={3000} onClose={()=>setSnack(null)} message={snack} />
    </FormProvider>
  );
}

function CommentFormDlg({ mode, comment, onSave, onClose }: { mode:"create"|"edit"; comment?: Comment; onSave:(d:Partial<Comment>)=>void; onClose:()=>void }) {
  const [f,setF]=useState({ rootTaskId:comment?.rootTaskId??"", authorName:comment?.authorName??"", text:comment?.text??"", entityType:comment?.entityType??"" });
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>{mode==="create"?"Add Comment":"Edit Comment"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt:0.5 }}>
          {mode==="create"&&<>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Root Task ID" value={f.rootTaskId} onChange={(e)=>setF({...f,rootTaskId:e.target.value})} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Author Name" value={f.authorName} onChange={(e)=>setF({...f,authorName:e.target.value})} size="small" /></Grid>
            <Grid item xs={12} sm={6}><TextField fullWidth label="Entity Type" value={f.entityType} onChange={(e)=>setF({...f,entityType:e.target.value})} size="small" /></Grid>
          </>}
          <Grid item xs={12}><TextField fullWidth label="Comment Text" value={f.text} onChange={(e)=>setF({...f,text:e.target.value})} size="small" multiline rows={3} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px:3,pb:2 }}>
        <Button onClick={onClose} sx={{ textTransform:"none" }}>Cancel</Button>
        <Button variant="contained" sx={{ textTransform:"none",bgcolor:PRIMARY }} onClick={()=>onSave({ rootTaskId:Number(f.rootTaskId)||undefined,authorName:f.authorName,text:f.text,entityType:f.entityType||undefined })}>
          {mode==="create"?"Add":"Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REPORTS TAB
// ─────────────────────────────────────────────────────────────────────────────

function KpiCard({ title, value, sub, color="#1a1a2e", bg="#f9fafb", icon, loading }: { title:string; value:string|number; sub?:string; color?:string; bg?:string; icon:React.ReactNode; loading?:boolean }) {
  if (loading) return <Card sx={{ height:"100%",borderRadius:3,border:"1px solid #f0f0f0" }}><CardContent sx={{ p:2.5 }}><Skeleton width="50%" height={14} /><Skeleton width="40%" height={44} sx={{ mt:0.5 }} /></CardContent></Card>;
  return (
    <Card sx={{ height:"100%",borderRadius:3,border:"1px solid #f0f0f0","&:hover":{ boxShadow:"0 4px 16px rgba(0,0,0,0.1)" },transition:"box-shadow 0.2s" }}>
      <CardContent sx={{ p:2.5,"&:last-child":{ pb:2.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform:"uppercase",letterSpacing:"0.06em",fontSize:"0.66rem" }}>{title}</Typography>
            <Typography variant="h4" fontWeight={800} sx={{ color,mt:0.25,lineHeight:1.1,fontSize:"1.6rem" }}>{value}</Typography>
            {sub&&<Typography variant="caption" color="text.secondary" sx={{ mt:0.25,display:"block" }}>{sub}</Typography>}
          </Box>
          <Box sx={{ width:44,height:44,borderRadius:2,bgcolor:bg,display:"flex",alignItems:"center",justifyContent:"center" }}>{icon}</Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ReportSection({ title, sub, children, exportData, exportName }: { title:string; sub:string; children:React.ReactNode; exportData?: Record<string,unknown>[]; exportName?:string }) {
  return (
    <Card sx={{ borderRadius:3,border:"1px solid #f0f0f0" }}>
      <CardContent sx={{ p:2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box><Typography variant="subtitle1" fontWeight={700}>{title}</Typography><Typography variant="caption" color="text.secondary">{sub}</Typography></Box>
          {exportData&&exportName&&<Tooltip title="Export CSV"><IconButton size="small" onClick={()=>exportCsv(exportName,exportData)} sx={{ color:PRIMARY }}><DownloadIcon fontSize="small" /></IconButton></Tooltip>}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

const STACK_C = { new:"#0288d1",pending:"#f59e0b",inProgress:"#7c3aed",completed:"#16a34a",cancelled:"#dc2626",onHold:"#6b7280" };

function TaskPerformanceTab({ data }: { data: TaskPerformanceReport }) {
  const w = useCW();
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="Completion Rate"  value={`${data.completionRate}%`} sub={`${data.completedThisMonth} completed this month`} color="#16a34a" bg="#e8f5e9" icon={<AssignmentTurnedInIcon sx={{ color:"#16a34a",fontSize:22 }} />} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Avg Cycle Time"   value={`${data.avgCycleTimeDays}d`} sub="Creation → completion"     color="#0288d1" bg="#e3f2fd" icon={<TimerIcon sx={{ color:"#0288d1",fontSize:22 }} />} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Overdue Tasks"    value={data.overdueCount} sub={`${data.overdueRate}% overdue rate`}  color="#dc2626" bg="#fff5f5" icon={<WarningAmberIcon sx={{ color:"#dc2626",fontSize:22 }} />} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Total Tasks"      value={data.totalTasks} sub="All time across all statuses"          color="#7c3aed" bg="#ede9fe" icon={<TrendingUpIcon sx={{ color:"#7c3aed",fontSize:22 }} />} /></Grid>
      </Grid>
      <ReportSection title="Task Type Breakdown by Status" sub="Compliance bottlenecks" exportData={data.taskTypeBreakdown as unknown as Record<string,unknown>[]} exportName="task-type-breakdown">
        <Box sx={{ height:w>=900?260:190 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.taskTypeBreakdown} layout="vertical" margin={{ top:0,right:20,left:0,bottom:0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize:11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="taskName" tick={{ fontSize:11 }} axisLine={false} tickLine={false} width={120} />
              <ReTooltip contentStyle={{ borderRadius:10,fontSize:12,border:"1px solid #e5e7eb" }} />
              <Legend iconSize={8} wrapperStyle={{ fontSize:11,paddingTop:8 }} />
              <Bar dataKey="completed" name="Completed" stackId="a" fill={STACK_C.completed} maxBarSize={18} />
              <Bar dataKey="inProgress" name="In Progress" stackId="a" fill={STACK_C.inProgress} maxBarSize={18} />
              <Bar dataKey="pending" name="Pending" stackId="a" fill={STACK_C.pending} maxBarSize={18} />
              <Bar dataKey="new" name="New" stackId="a" fill={STACK_C.new} maxBarSize={18} />
              <Bar dataKey="cancelled" name="Cancelled" stackId="a" fill={STACK_C.cancelled} maxBarSize={18} />
              <Bar dataKey="onHold" name="On Hold" stackId="a" fill={STACK_C.onHold} maxBarSize={18} radius={[0,3,3,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </ReportSection>
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <ReportSection title="Weekly Throughput" sub="Tasks created vs completed per week (last 10 weeks)" exportData={data.weeklyTrend as unknown as Record<string,unknown>[]} exportName="weekly-throughput">
            <Box sx={{ height:w>=900?200:160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.weeklyTrend} margin={{ top:5,right:5,bottom:0,left:-20 }}>
                  <defs>
                    <linearGradient id="rp_gc" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#16a34a" stopOpacity={0.25} /><stop offset="95%" stopColor="#16a34a" stopOpacity={0.02} /></linearGradient>
                    <linearGradient id="rp_gn" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#7c3aed" stopOpacity={0.25} /><stop offset="95%" stopColor="#7c3aed" stopOpacity={0.02} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                  <ReTooltip contentStyle={{ borderRadius:10,fontSize:12 }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize:11 }} />
                  <Area type="monotone" dataKey="created" name="Created" stroke="#7c3aed" strokeWidth={2} fill="url(#rp_gn)" dot={false} />
                  <Area type="monotone" dataKey="completed" name="Completed" stroke="#16a34a" strokeWidth={2} fill="url(#rp_gc)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </ReportSection>
        </Grid>
        <Grid item xs={12} md={5}>
          <ReportSection title="Completion Rate by Category" sub="Percentage of tasks completed per category" exportData={data.categoryRates as unknown as Record<string,unknown>[]} exportName="category-rates">
            <Stack spacing={1.5} mt={1}>
              {data.categoryRates.map((r) => (
                <Box key={r.category}>
                  <Stack direction="row" justifyContent="space-between" mb={0.5}>
                    <Typography variant="caption" fontWeight={600}>{r.category}</Typography>
                    <Typography variant="caption" fontWeight={700} color={r.rate>=50?"success.main":"error.main"}>{r.rate.toFixed(1)}%</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={r.rate} sx={{ height:8,borderRadius:4,bgcolor:"#f0f0f0","& .MuiLinearProgress-bar":{ bgcolor:r.rate>=50?"#16a34a":"#f59e0b",borderRadius:4 } }} />
                  <Typography variant="caption" color="text.disabled">{r.completed}/{r.total} tasks</Typography>
                </Box>
              ))}
            </Stack>
          </ReportSection>
        </Grid>
      </Grid>
    </Stack>
  );
}

function CueNoteSlaTab({ data }: { data: CueNoteSlaReport }) {
  const w = useCW();
  const rankColor = (i: number) => ["#f59e0b","#6b7280","#cd7f32","#0288d1","#16a34a"][i] ?? "#0288d1";
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><KpiCard title="SLA Compliance" value={`${data.slaComplianceRate}%`} sub="Notes replied within 24h"   color="#16a34a" bg="#e8f5e9" icon={<SpeedIcon sx={{ color:"#16a34a",fontSize:22 }} />} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Avg Reply Time" value={`${data.avgReplyHours}h`}    sub="Average hours to first reply" color="#0288d1" bg="#e3f2fd" icon={<TimerIcon sx={{ color:"#0288d1",fontSize:22 }} />} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="Overdue Notes"  value={data.overdueNotes}           sub="Notes past due without reply" color="#dc2626" bg="#fff5f5" icon={<WarningAmberIcon sx={{ color:"#dc2626",fontSize:22 }} />} /></Grid>
        <Grid item xs={6} sm={3}><KpiCard title="This Month"     value={data.totalThisMonth}         sub="Cue notes created this month"  color="#7c3aed" bg="#ede9fe" icon={<NotesIcon sx={{ color:"#7c3aed",fontSize:22 }} />} /></Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <ReportSection title="SLA Tier Distribution" sub="Reply time breakdown across all cue notes">
            <Grid container spacing={2} sx={{ mt:0.5 }}>
              {data.slaTiers.map((t) => (
                <Grid key={t.label} item xs={6}>
                  <Box sx={{ p:1.5,borderRadius:2,border:`2px solid ${t.color}30`,bgcolor:`${t.color}08`,textAlign:"center" }}>
                    <Typography variant="h5" fontWeight={800} sx={{ color:t.color }}>{t.count}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </ReportSection>
        </Grid>
        <Grid item xs={12} md={7}>
          <ReportSection title="Weekly Reply Volume" sub="Notes created vs replied per week" exportData={data.weeklyReply as unknown as Record<string,unknown>[]} exportName="weekly-reply">
            <Box sx={{ height:w>=900?220:170 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.weeklyReply} margin={{ top:5,right:5,bottom:0,left:-20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="week" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                  <ReTooltip contentStyle={{ borderRadius:10,fontSize:12 }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize:11 }} />
                  <Bar dataKey="created" name="Created" fill="#7c3aed" maxBarSize={20} />
                  <Bar dataKey="replied" name="Replied" fill="#16a34a" maxBarSize={20} radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ReportSection>
        </Grid>
      </Grid>
      <ReportSection title="Top Responders" sub="Staff ranked by reply count and speed" exportData={data.topResponders as unknown as Record<string,unknown>[]} exportName="top-responders">
        <Grid container spacing={1.5} sx={{ mt:0.5 }}>
          {data.topResponders.map((r,i) => (
            <Grid key={r.name} item xs={12} sm={6} md={3}>
              <Box sx={{ p:2,borderRadius:2,border:"1px solid #f0f0f0",bgcolor:"#f9fafb" }}>
                <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
                  <Box sx={{ width:28,height:28,borderRadius:"50%",bgcolor:rankColor(i),display:"flex",alignItems:"center",justifyContent:"center" }}>
                    <Typography variant="caption" fontWeight={800} sx={{ color:"#fff",fontSize:"0.75rem" }}>#{i+1}</Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={700} noWrap>{r.name}</Typography>
                </Stack>
                <Chip label={`${r.count} replies`} size="small" sx={{ height:20,fontSize:"0.68rem",bgcolor:`${rankColor(i)}15`,color:rankColor(i),fontWeight:700,mr:0.5 }} />
                <Chip label={`${r.avgHours}h avg`} size="small" sx={{ height:20,fontSize:"0.68rem" }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </ReportSection>
    </Stack>
  );
}

function PlacementRiskTab({ data }: { data: PlacementRiskReport }) {
  const riskColor = { high:"#dc2626",medium:"#f59e0b",low:"#16a34a" };
  const riskBg    = { high:"#fff5f5",medium:"#fffbeb",low:"#f0fdf4" };
  return (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        {(["high","medium","low"] as const).map((level) => (
          <Grid key={level} item xs={4}>
            <Box sx={{ p:2,borderRadius:2,border:`2px solid ${riskColor[level]}30`,bgcolor:riskBg[level],textAlign:"center" }}>
              <Typography variant="h4" fontWeight={800} sx={{ color:riskColor[level] }}>{data.riskSummary[level]}</Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color:riskColor[level],textTransform:"capitalize" }}>{level} Risk</Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
      <ReportSection title="Placement Risk Details" sub="All placements ranked by compliance completion rate" exportData={data.placements as unknown as Record<string,unknown>[]} exportName="placement-risk">
        <TableContainer component={Paper} elevation={0} sx={{ border:"1px solid #f0f0f0",borderRadius:2,overflowX:"auto",mt:1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th":{ fontWeight:700,fontSize:"0.72rem",color:"text.secondary",textTransform:"uppercase",bgcolor:"#f8f9fb" } }}>
                <TableCell>Placement</TableCell><TableCell>Candidate</TableCell>
                <TableCell align="center">Total</TableCell><TableCell align="center">Open</TableCell>
                <TableCell align="center">Overdue</TableCell><TableCell align="center">Completion</TableCell>
                <TableCell>Risk</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.placements.map((p) => (
                <TableRow key={p.placementId} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>#{p.placementId}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{p.candidateName}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2">{p.totalTasks}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2" color={p.openTasks>2?"error.main":"text.primary"} fontWeight={p.openTasks>2?700:400}>{p.openTasks}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2" color={p.overdueTasks>0?"error.main":"text.primary"} fontWeight={p.overdueTasks>0?700:400}>{p.overdueTasks}</Typography></TableCell>
                  <TableCell align="center">
                    <Box sx={{ display:"flex",alignItems:"center",gap:1,justifyContent:"center" }}>
                      <LinearProgress variant="determinate" value={p.completionRate} sx={{ width:60,height:6,borderRadius:3,bgcolor:"#f0f0f0","& .MuiLinearProgress-bar":{ bgcolor:p.completionRate>=70?"#16a34a":p.completionRate>=40?"#f59e0b":"#dc2626",borderRadius:3 } }} />
                      <Typography variant="caption" fontWeight={700}>{p.completionRate.toFixed(0)}%</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Chip label={p.riskLevel} size="small" sx={{ bgcolor:riskBg[p.riskLevel],color:riskColor[p.riskLevel],fontWeight:700,fontSize:"0.7rem",height:20,textTransform:"capitalize" }} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ReportSection>
    </Stack>
  );
}

function StaffActivityTab({ data }: { data: StaffActivityReport }) {
  const w = useCW();
  return (
    <Stack spacing={2.5}>
      <ReportSection title="Staff Activity Summary" sub="Actions performed by each team member" exportData={data.staffActivity as unknown as Record<string,unknown>[]} exportName="staff-activity">
        <TableContainer component={Paper} elevation={0} sx={{ border:"1px solid #f0f0f0",borderRadius:2,overflowX:"auto",mt:1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th":{ fontWeight:700,fontSize:"0.72rem",color:"text.secondary",textTransform:"uppercase",bgcolor:"#f8f9fb" } }}>
                <TableCell>Staff Member</TableCell><TableCell align="center">Total</TableCell>
                <TableCell align="center">Completed</TableCell><TableCell align="center">Created</TableCell>
                <TableCell align="center">Updated</TableCell><TableCell align="center">Cancelled</TableCell>
                <TableCell>Last Active</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.staffActivity.map((s) => (
                <TableRow key={s.name} hover>
                  <TableCell><Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width:28,height:28,bgcolor:`${PRIMARY}18`,color:PRIMARY,fontSize:"0.7rem",fontWeight:700 }}>{s.name.split(" ").map((n)=>n[0]).join("").slice(0,2)}</Avatar>
                    <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                  </Stack></TableCell>
                  <TableCell align="center"><Typography variant="body2" fontWeight={700}>{s.total}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2" color="success.main" fontWeight={600}>{s.completed}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2">{s.created}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2">{s.updated}</Typography></TableCell>
                  <TableCell align="center"><Typography variant="body2" color="error.main">{s.cancelled}</Typography></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{s.lastActive?formatDistanceToNow(new Date(s.lastActive),{ addSuffix:true }):"—"}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ReportSection>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <ReportSection title="Daily Activity Heatmap" sub="Total actions per day across all entity types" exportData={data.dailyActivity as unknown as Record<string,unknown>[]} exportName="daily-activity">
            <Box sx={{ height:w>=900?240:180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.dailyActivity} margin={{ top:5,right:5,bottom:0,left:-20 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="day" tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize:10 }} axisLine={false} tickLine={false} />
                  <ReTooltip contentStyle={{ borderRadius:10,fontSize:12 }} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize:11 }} />
                  <Bar dataKey="tasks" name="Tasks" stackId="a" fill="#7c3aed" maxBarSize={24} />
                  <Bar dataKey="notes" name="Cue Notes" stackId="a" fill="#0288d1" maxBarSize={24} />
                  <Bar dataKey="comments" name="Comments" stackId="a" fill="#16a34a" maxBarSize={24} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ReportSection>
        </Grid>
        <Grid item xs={12} md={4}>
          <ReportSection title="Action Breakdown" sub="All activity by action type">
            <Box sx={{ position:"relative",height:180 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.actionBreakdown} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="count" nameKey="action" paddingAngle={2} isAnimationActive={false}>
                    {data.actionBreakdown.map((_,i) => <Cell key={i} fill={CHART_COLORS[i%8]} stroke="none" />)}
                  </Pie>
                  <ReTooltip contentStyle={{ borderRadius:10,fontSize:12 }} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Stack spacing={0.75} mt={0.5}>
              {data.actionBreakdown.slice(0,6).map((a,i) => (
                <Stack key={a.action} direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width:8,height:8,borderRadius:"50%",bgcolor:CHART_COLORS[i%8] }} />
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform:"capitalize" }}>{a.action.replace("_"," ")}</Typography>
                  </Stack>
                  <Typography variant="caption" fontWeight={700}>{a.count}</Typography>
                </Stack>
              ))}
            </Stack>
          </ReportSection>
        </Grid>
      </Grid>
    </Stack>
  );
}

const REPORT_TABS = [
  { value:"tasks",    label:"Task Performance", icon:<AssignmentTurnedInIcon sx={{ fontSize:16 }} /> },
  { value:"sla",      label:"Cue Note SLA",     icon:<SpeedIcon sx={{ fontSize:16 }} /> },
  { value:"risk",     label:"Placement Risk",   icon:<LocalFireDepartmentIcon sx={{ fontSize:16 }} /> },
  { value:"staff",    label:"Staff Activity",   icon:<PeopleIcon sx={{ fontSize:16 }} /> },
];

function ReportsTab({ taskPerf, sla, risk, staff, loading, reportsResolvedSources }: { taskPerf:TaskPerformanceReport; sla:CueNoteSlaReport; risk:PlacementRiskReport; staff:StaffActivityReport; loading?: boolean; reportsResolvedSources?: ReadonlySet<string> }) {
  const [tab,setTab]=useState("tasks");
  const w = useCW();
  const r = reportsResolvedSources;
  const has = (s: string) => r?.has(s) ?? false;
  const loadingTaskPerf = r ? !has('task-performance') : (loading ?? false);
  const loadingSla = r ? !has('cue-note-sla') : (loading ?? false);
  const loadingRisk = r ? !has('placement-risk') : (loading ?? false);
  const loadingStaff = r ? !has('staff-activity') : (loading ?? false);
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems={w>=600?"center":"flex-start"} mb={3} gap={1} flexWrap="wrap">
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing:"-0.02em",fontSize:w>=600?"1.5rem":"1.15rem" }}>Reports & Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Deep-dive insights into task performance, SLA compliance, placement risk, and team productivity</Typography>
        </Box>
      </Stack>
      <Box sx={{ mb:3,borderBottom:"2px solid #f0f0f0" }}>
        <Tabs value={tab} onChange={(_,v)=>setTab(v)} variant="scrollable" scrollButtons="auto"
          TabIndicatorProps={{ sx:{ height:3,borderRadius:"3px 3px 0 0",bgcolor:PRIMARY } }} sx={{ minHeight:44 }}>
          {REPORT_TABS.map((t) => (
            <Tab key={t.value} value={t.value}
              label={<Stack direction="row" spacing={0.75} alignItems="center">{t.icon}<span>{t.label}</span></Stack>}
              sx={{ minHeight:44,textTransform:"none",fontWeight:600,fontSize:"0.85rem",color:"text.secondary","&.Mui-selected":{ color:PRIMARY } }} />
          ))}
        </Tabs>
      </Box>
      {tab==="tasks" && (loadingTaskPerf ? (
        <Card sx={{ borderRadius:3,border:"1px solid #f0f0f0",p:3 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid></Grid>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius:2 }} />
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius:2 }} />
          </Stack>
        </Card>
      ) : <TaskPerformanceTab data={taskPerf} />)}
      {tab==="sla"   && (loadingSla ? (
        <Card sx={{ borderRadius:3,border:"1px solid #f0f0f0",p:3 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid></Grid>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius:2 }} />
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius:2 }} />
          </Stack>
        </Card>
      ) : <CueNoteSlaTab data={sla} />)}
      {tab==="risk"  && (loadingRisk ? (
        <Card sx={{ borderRadius:3,border:"1px solid #f0f0f0",p:3 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid></Grid>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius:2 }} />
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius:2 }} />
          </Stack>
        </Card>
      ) : <PlacementRiskTab data={risk} />)}
      {tab==="staff" && (loadingStaff ? (
        <Card sx={{ borderRadius:3,border:"1px solid #f0f0f0",p:3 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid><Grid item xs={6} sm={3}><Skeleton variant="rectangular" height={80} sx={{ borderRadius:2 }} /></Grid></Grid>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius:2 }} />
            <Skeleton variant="rectangular" height={150} sx={{ borderRadius:2 }} />
          </Stack>
        </Card>
      ) : <StaffActivityTab data={staff} />)}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ACTIVITY LOG TAB
// ─────────────────────────────────────────────────────────────────────────────

const ACT_CFG: Record<string,{ color:string; icon:React.ReactNode; label:string }> = {
  external_task:{ color:"#3f2d8f",icon:<AssignmentIcon sx={{ fontSize:16 }} />,label:"External Task" },
  cue_note:     { color:"#0288d1",icon:<NotesIcon sx={{ fontSize:16 }} />,     label:"Cue Note"      },
  comment:      { color:"#2e7d32",icon:<CommentIcon sx={{ fontSize:16 }} />,   label:"Comment"       },
};

function ActivityLogTab({ activity, loading }: { activity: ActivityEntry[]; loading?: boolean }) {
  const [limit,setLimit]=useState(50); const [typeFilter,setTypeFilter]=useState("");
  const filtered = useMemo(() => {
    const list = [...activity].sort((a,b)=>new Date(b.performedAt).getTime()-new Date(a.performedAt).getTime());
    return (typeFilter ? list.filter((a)=>a.entityType===typeFilter) : list).slice(0,limit);
  },[activity,limit,typeFilter]);
  const grouped: Record<string,ActivityEntry[]> = {};
  filtered.forEach((a) => { const day=format(new Date(a.performedAt),"MMMM d, yyyy"); if(!grouped[day])grouped[day]=[]; grouped[day].push(a); });

  const w = useCW();
  if (loading) {
    return (
      <Box>
        <Box sx={{ display:"flex",flexDirection:w>=600?"row":"column",alignItems:w>=600?"center":"flex-start",justifyContent:"space-between",mb:3,gap:1.5 }}>
          <Box><Typography variant="h5" fontWeight={700} sx={{ fontSize:w>=600?"1.5rem":"1.15rem" }}>Activity Log</Typography><Typography variant="body2" color="text.secondary">Full audit trail of actions across all entities</Typography></Box>
        </Box>
        <Card elevation={0} sx={{ border:"1px solid",borderColor:"divider",borderRadius:2 }}>
          <CardContent sx={{ p:2 }}>
            <Stack spacing={1.5}>{[1,2,3,4,5,6].map((i)=>(<Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius:1 }} />))}</Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }
  return (
    <Box>
      <Box sx={{ display:"flex",flexDirection:w>=600?"row":"column",alignItems:w>=600?"center":"flex-start",justifyContent:"space-between",mb:3,gap:1.5 }}>
        <Box><Typography variant="h5" fontWeight={700} sx={{ fontSize:w>=600?"1.5rem":"1.15rem" }}>Activity Log</Typography><Typography variant="body2" color="text.secondary">Full audit trail of actions across all entities</Typography></Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <TextField select size="small" value={typeFilter} onChange={(e)=>setTypeFilter(e.target.value)}
            sx={{ width:w>=600?160:150,"& .MuiOutlinedInput-root":{ height:34,borderRadius:2,fontSize:"0.875rem" } }} SelectProps={{ displayEmpty:true }}>
            <MenuItem value=""><em>All Types</em></MenuItem>
            <MenuItem value="external_task">External Tasks</MenuItem>
            <MenuItem value="cue_note">Cue Notes</MenuItem>
            <MenuItem value="comment">Comments</MenuItem>
          </TextField>
          <TextField select size="small" value={limit} onChange={(e)=>setLimit(Number(e.target.value))}
            sx={{ width:110,"& .MuiOutlinedInput-root":{ height:34,borderRadius:2,fontSize:"0.875rem" } }}>
            {[20,50,100,200].map((l)=><MenuItem key={l} value={l}>Last {l}</MenuItem>)}
          </TextField>
        </Stack>
      </Box>
      <Card elevation={0} sx={{ border:"1px solid",borderColor:"divider",borderRadius:2 }}>
        <CardContent sx={{ p:0 }}>
          {filtered.length===0 ? (
            <Box sx={{ textAlign:"center",py:6 }}><Typography variant="body2" color="text.secondary">No activity to show</Typography></Box>
          ) : Object.entries(grouped).map(([day,entries],gi) => (
            <Box key={day}>
              {gi>0&&<Divider />}
              <Box sx={{ px:2,py:1,bgcolor:"#f8f9fb" }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing="0.08em">{day}</Typography>
              </Box>
              <List dense disablePadding>
                {entries.map((a,i) => {
                  const cfg=ACT_CFG[a.entityType]??ACT_CFG.comment;
                  return (
                    <Box key={a.id}>
                      {i>0&&<Divider component="li" sx={{ ml:7 }} />}
                      <ListItem alignItems="flex-start" sx={{ py:1.25,px:2 }}>
                        <ListItemAvatar sx={{ minWidth:40,mt:0 }}>
                          <Avatar sx={{ width:32,height:32,bgcolor:`${cfg.color}18`,color:cfg.color }}>{cfg.icon}</Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Typography variant="body2" fontWeight={500}>{a.description}</Typography>
                              <Chip label={cfg.label} size="small" sx={{ height:18,fontSize:"0.65rem",fontWeight:600,bgcolor:`${cfg.color}15`,color:cfg.color }} />
                              <Chip label={`#${a.entityId}`} size="small" variant="outlined" sx={{ height:18,fontSize:"0.65rem" }} />
                            </Stack>
                          }
                          secondary={
                            <Stack component="span" direction="row" spacing={1} alignItems="center" mt={0.25}>
                              <Typography component="span" variant="caption" fontWeight={600} color="text.primary">{a.performedBy}</Typography>
                              <Typography component="span" variant="caption" color="text.disabled">·</Typography>
                              <Tooltip title={format(new Date(a.performedAt),"PPpp")}>
                                <Typography component="span" variant="caption" color="text.secondary">{formatDistanceToNow(new Date(a.performedAt),{ addSuffix:true })}</Typography>
                              </Tooltip>
                            </Stack>
                          }
                        />
                      </ListItem>
                    </Box>
                  );
                })}
              </List>
            </Box>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const NAV_TABS = [
  { value:"overview",       label:"Overview"      },
  { value:"external-tasks", label:"External Tasks"},
  { value:"cue-notes",      label:"Cue Notes"     },
  { value:"comments",       label:"Comments"      },
  { value:"sms",            label:"SMS"           },
  { value:"reports",        label:"Reports"       },
  { value:"activity",       label:"Activity Log"  },
];

export default function AdminDashboard({
  data,
  callbacks,
  defaultTab = "comments",
  showNavigation = true,
  dateRange,
  onDateRangeChange,
  onTabChange,
  onFetchTasksPage,
  onFetchCueNotesPage,
  onFetchCommentsPage,
  duration: durationProp,
  onDurationChange,
  loadingTab,
  loadingGrid,
  overviewResolvedSources,
  reportsResolvedSources,
}: AdminDashboardProps) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [durationLocal, setDurationLocal] = useState<DurationValue>(DEFAULT_DURATION);
  const duration = durationProp ?? durationLocal;
  const setDuration = onDurationChange ? (d: DurationValue) => onDurationChange(d, activeTab) : setDurationLocal;

  const handleTabChange = useCallback((_e: React.SyntheticEvent, value: string) => {
    setActiveTab(value);
    onTabChange?.(value as "overview" | "external-tasks" | "cue-notes" | "comments" | "sms" | "reports" | "activity");
  }, [onTabChange]);

  // Container-width ResizeObserver — feeds DashboardWidthCtx so all children use container px not viewport
  const rootRef = useRef<HTMLDivElement>(null);
  const [cw, setCw] = useState(1200);
  useEffect(() => {
    const el = rootRef.current; if(!el) return;
    const ro = new ResizeObserver(([e]) => setCw(e.contentRect.width));
    ro.observe(el); return () => ro.disconnect();
  }, []);

  // Live data state — initialised from props, no static fallback (show empty until API returns)
  const [tasks,    setTasks]    = useState<ExternalTask[]>(() => data?.tasks    ?? []);
  const [notes,    setNotes]    = useState<CueNote[]>(     () => data?.cueNotes ?? []);
  const [comments, setComments] = useState<Comment[]>(     () => data?.comments ?? []);
  const [activity, setActivity] = useState<ActivityEntry[]>(() => data?.activityLog ?? []);

  // Sync when parent data prop changes (for dynamic / server-driven integration)
  useEffect(() => { if (data?.tasks)      setTasks(data.tasks);           }, [data?.tasks]);
  useEffect(() => { if (data?.cueNotes)   setNotes(data.cueNotes);        }, [data?.cueNotes]);
  useEffect(() => { if (data?.comments)   setComments(data.comments);     }, [data?.comments]);
  useEffect(() => { if (data?.activityLog)setActivity(data.activityLog);  }, [data?.activityLog]);

  // Derived / computed values — live from state unless parent provides overrides
  const overview      = useMemo(() => data?.overview              ?? computeOverview(tasks,notes,comments),   [data?.overview,tasks,notes,comments]);
  const taskStats     = useMemo(() => data?.taskStats             ?? computeTaskStats(tasks),                  [data?.taskStats,tasks]);
  const cueNoteStats  = useMemo(() => data?.cueNoteStats          ?? computeCueNoteStats(notes),               [data?.cueNoteStats,notes]);
  const commentStats  = useMemo(() => data?.commentStats          ?? computeCommentStats(comments),            [data?.commentStats,comments]);
  const taskPerf      = data?.taskPerformanceReport  ?? EMPTY_TASK_PERF;
  const slaReport     = data?.cueNoteSlaReport       ?? EMPTY_SLA;
  const riskReport    = data?.placementRiskReport    ?? EMPTY_RISK;
  const staffReport   = data?.staffActivityReport    ?? EMPTY_STAFF;

  // ── Mutation handlers (optimistic local + optional external callback) ──────

  const handleCreateTask = useCallback(async (d: Partial<ExternalTask>) => {
    const now = new Date().toISOString();
    const task: ExternalTask = { id:`t${nextId()}`, placementId:Number(d.placementId)||0, taskName:d.taskName??"", taskCategory:d.taskCategory, candidateName:d.candidateName, statusId:d.statusId??1, statusName:STATUS_MAP[d.statusId??1]??"New", dueDate:d.dueDate, notes:d.notes, createdAt:now, updatedAt:now };
    setTasks((p) => [task,...p]);
    await callbacks?.onCreateTask?.(d);
  }, [callbacks]);

  const handleUpdateTask = useCallback(async (id: string, d: Partial<ExternalTask>) => {
    setTasks((p) => p.map((t) => t.id===id ? { ...t,...d, statusName:d.statusId?STATUS_MAP[d.statusId]??t.statusName:t.statusName, updatedAt:new Date().toISOString() } : t));
    await callbacks?.onUpdateTask?.(id, d);
  }, [callbacks]);

  const handleDeleteTask = useCallback(async (id: string) => {
    setTasks((p) => p.filter((t) => t.id!==id));
    await callbacks?.onDeleteTask?.(id);
  }, [callbacks]);

  const handleBulkUpdateTaskStatus = useCallback(async (ids: string[], statusId: number) => {
    setTasks((p) => p.map((t) => ids.includes(t.id) ? { ...t,statusId,statusName:STATUS_MAP[statusId]??t.statusName,updatedAt:new Date().toISOString() } : t));
    await callbacks?.onBulkUpdateTaskStatus?.(ids, statusId);
  }, [callbacks]);

  const handleBulkCancelByPlacement = useCallback(async (placementId: number) => {
    setTasks((p) => p.map((t) => t.placementId===placementId&&![4,5].includes(t.statusId) ? { ...t,statusId:5,statusName:"Cancelled",updatedAt:new Date().toISOString() } : t));
    await callbacks?.onBulkCancelTasksByPlacement?.(placementId);
  }, [callbacks]);

  const handleCopyTasks = useCallback(async (from: number, to: number) => {
    const now = new Date().toISOString();
    const copies = tasks.filter((t)=>t.placementId===from).map((t)=>({ ...t,id:`t${nextId()}`,placementId:to,statusId:1,statusName:"New",completedDate:undefined,createdAt:now,updatedAt:now }));
    setTasks((p) => [...copies,...p]);
    await callbacks?.onCopyTasks?.(from, to);
  }, [callbacks, tasks]);

  const handleMarkReplied = useCallback(async (id: number, text: string, by: string) => {
    const now = new Date().toISOString();
    setNotes((p) => p.map((n) => n.taskId===id ? { ...n,isReplied:true,repliedAt:now,repliedByName:by } : n));
    await callbacks?.onMarkCueNoteReplied?.(id, text, by);
  }, [callbacks]);

  const handleCreateNote = useCallback(async (d: Partial<CueNote>) => {
    const now = new Date().toISOString();
    const note: CueNote = { taskId:nextId(), placementId:Number(d.placementId)||0, senderName:d.senderName??"", text:d.text??"", isReplied:false, createdAt:now, dueDate:d.dueDate };
    setNotes((p) => [note,...p]);
    await callbacks?.onCreateCueNote?.(d);
  }, [callbacks]);

  const handleUpdateNote = useCallback(async (id: number, d: Partial<CueNote>) => {
    setNotes((p) => p.map((n) => n.taskId===id ? { ...n,...d } : n));
    await callbacks?.onUpdateCueNote?.(id, d);
  }, [callbacks]);

  const handleDeleteNote = useCallback(async (id: number) => {
    setNotes((p) => p.filter((n) => n.taskId!==id));
    await callbacks?.onDeleteCueNote?.(id);
  }, [callbacks]);

  const handleBulkCreateNotes = useCallback(async (pids: number[], text: string) => {
    const now = new Date().toISOString();
    const newNotes = pids.map((pid) => ({ taskId:nextId(), placementId:pid, senderName:"You", text, isReplied:false, createdAt:now }));
    setNotes((p) => [...newNotes,...p]);
    await callbacks?.onBulkCreateCueNotes?.(pids, text);
  }, [callbacks]);

  const handleBulkCancelNotes = useCallback(async (ids: number[]) => {
    setNotes((p) => p.filter((n) => !ids.includes(n.taskId)));
    await callbacks?.onBulkCancelCueNotes?.(ids);
  }, [callbacks]);

  const handleCreateComment = useCallback(async (d: Partial<Comment>) => {
    const now = new Date().toISOString();
    const c: Comment = { taskId:nextId(), authorName:d.authorName??"", text:d.text??"", isEdited:false, createdAt:now, rootTaskId:d.rootTaskId, entityType:d.entityType };
    setComments((p) => [c,...p]);
    await callbacks?.onCreateComment?.(d);
  }, [callbacks]);

  const handleUpdateComment = useCallback(async (id: number, text: string) => {
    setComments((p) => p.map((c) => c.taskId===id ? { ...c,text,isEdited:true,editedAt:new Date().toISOString() } : c));
    await callbacks?.onUpdateComment?.(id, text);
  }, [callbacks]);

  const handleDeleteComment = useCallback(async (id: number) => {
    setComments((p) => p.filter((c) => c.taskId!==id));
    await callbacks?.onDeleteComment?.(id);
  }, [callbacks]);

  const handleBulkDeleteComments = useCallback(async (ids: number[]) => {
    setComments((p) => p.filter((c) => !ids.includes(c.taskId)));
    await callbacks?.onBulkDeleteComments?.(ids);
  }, [callbacks]);

  return (
    <DashboardWidthCtx.Provider value={cw}>
    <Box ref={rootRef} sx={{ width:"100%",bgcolor:"#f9fafb", display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {showNavigation && (
        <Box sx={{ borderBottom:"1px solid",borderColor:"divider",bgcolor:"#fff",px:2,display:"flex",alignItems:"center",flexWrap:"wrap",gap:1 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto"
            sx={{
              minHeight:44,
              flex:1,
              minWidth:0,
              "& .MuiTab-root":{ minHeight:44,py:1.25,fontSize:"0.8rem",textTransform:"none",alignSelf:"center",alignItems:"center",justifyContent:"center" },
              "& .MuiTab-wrapper":{ alignItems:"center",justifyContent:"center" },
              "& .MuiTabs-indicator":{ bgcolor:PRIMARY }
            }}>
            {NAV_TABS.map((t) => <Tab key={t.value} value={t.value} label={t.label} />)}
          </Tabs>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py:0.5 }}>
            <Select
              size="small"
              value={duration}
              onChange={(e) => setDuration(e.target.value as DurationValue)}
              displayEmpty
              sx={{ minWidth:120,"& .MuiSelect-select":{ py:0.65,fontSize:"0.8rem" } }}
            >
              {DURATION_OPTIONS.map((o)=> (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </Stack>
        </Box>
      )}

      <Box sx={{ p:{ xs:1.5,sm:2,md:3 }, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {activeTab==="overview" && (
          <OverviewTab overview={overview} categoryBreakdown={data?.categoryBreakdown ?? []} activity={activity} tasks={tasks} notes={notes} loading={loadingTab==="overview"} overviewResolvedSources={overviewResolvedSources} />
        )}
        {activeTab==="external-tasks" && (
          <ExternalTasksTab tasks={tasks} taskStats={taskStats} tasksTotal={data?.tasksTotal}
            onFetchPage={onFetchTasksPage}
            onUpdate={handleUpdateTask} onDelete={handleDeleteTask} onCreate={handleCreateTask}
            onBulkStatus={handleBulkUpdateTaskStatus} onBulkCancel={handleBulkCancelByPlacement} onCopy={handleCopyTasks}
            loading={loadingTab==="external-tasks"} gridLoading={loadingTab==="external-tasks"||loadingGrid==="external-tasks"} />
        )}
        {activeTab==="cue-notes" && (
          <CueNotesTab notes={notes} cueNoteStats={cueNoteStats} cueNotesTotal={data?.cueNotesTotal}
            onFetchPage={onFetchCueNotesPage}
            onMarkReplied={handleMarkReplied} onCreate={handleCreateNote} onUpdate={handleUpdateNote}
            onDelete={handleDeleteNote} onBulkCreate={handleBulkCreateNotes} onBulkCancel={handleBulkCancelNotes}
            loading={loadingTab==="cue-notes"} gridLoading={loadingTab==="cue-notes"||loadingGrid==="cue-notes"} />
        )}
        {activeTab==="comments" && (
          <CommentsTab comments={comments} commentStats={commentStats} commentsTotal={data?.commentsTotal}
            onFetchPage={onFetchCommentsPage}
            onCreate={handleCreateComment} onUpdate={handleUpdateComment}
            onDelete={handleDeleteComment} onBulkDelete={handleBulkDeleteComments}
            loading={loadingTab==="comments"} gridLoading={loadingTab==="comments"||loadingGrid==="comments"} />
        )}
        {activeTab==="sms" && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <DashboardSmsTab smsRecruiters={data?.smsRecruiters} loading={loadingTab==="sms"} />
          </Box>
        )}
        {activeTab==="reports" && (
          <ReportsTab taskPerf={taskPerf} sla={slaReport} risk={riskReport} staff={staffReport} loading={loadingTab==="reports"} reportsResolvedSources={reportsResolvedSources} />
        )}
        {activeTab==="activity" && (
          <ActivityLogTab activity={activity} loading={loadingTab==="activity"} />
        )}
      </Box>
    </Box>
    </DashboardWidthCtx.Provider>
  );
}
